/**
 * GetClearance Verification SDK
 * ==============================
 * Premium verification flow - best-in-class quality UX.
 *
 * Features:
 * - Visual document type selector with icons
 * - Camera overlay with face/document guides
 * - Real-time feedback messages
 * - Smooth animations and transitions
 * - Mobile-optimized responsive design
 */

import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
  Shield,
  Camera,
  FileText,
  CheckCircle,
  CheckCircle2,
  AlertCircle,
  Loader2,
  ChevronRight,
  ChevronLeft,
  Upload,
  RefreshCw,
  X,
  Smartphone,
  Sun,
  Eye,
  User,
  CreditCard,
  Car,
  Globe,
  Zap,
  Lock,
  Sparkles,
  MapPin,
  Calendar,
  Mail,
  Phone,
  UserCircle,
  ClipboardList,
  Scan,
  ShieldCheck,
  Wallet,
} from 'lucide-react';
import { BRAND } from '../../config/brand';

// Import step components
import LivenessStep from './steps/LivenessStep';
import MediaPipeLivenessStep from './steps/MediaPipeLivenessStep';
import PassiveLivenessStep from './steps/passive-liveness/PassiveLivenessStep';
import WalletAddressStep from './steps/WalletAddressStep';
import FlowSelectorStep from './steps/FlowSelectorStep';
// KYB step components
import CompanyInfoStep from './steps/CompanyInfoStep';
import UBOCollectionStep from './steps/UBOCollectionStep';
import CorporateDocumentsStep from './steps/CorporateDocumentsStep';
import KYBReviewStep from './steps/KYBReviewStep';
import { resizeImageIfNeeded } from '../../utils/imageResize';
import { useSDKEmbed, useReportStepError } from './embed/EmbedContext';
import { toCompleteStatus } from './embed/embedBridge';

// Branding utilities
const hexToRgb = (hex) => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result
    ? `${parseInt(result[1], 16)}, ${parseInt(result[2], 16)}, ${parseInt(result[3], 16)}`
    : '13, 148, 136';
};

// SDK API client
const createSDKClient = (accessToken, baseUrl = '/api/v1/sdk') => {
  const headers = {
    'Authorization': `Bearer ${accessToken}`,
    'Content-Type': 'application/json',
  };

  // Helper to properly append path to URL that may have query params
  // e.g., "/api/v1/sdk?reset=true" + "/config" -> "/api/v1/sdk/config?reset=true"
  const buildUrl = (path, extraParams = {}) => {
    let url = baseUrl;
    let queryString = '';

    // Extract any existing query params from baseUrl
    if (baseUrl.includes('?')) {
      const [base, params] = baseUrl.split('?');
      url = base;
      queryString = params;
    }

    // Append the path
    url = `${url}${path}`;

    // Add extra params
    const allParams = new URLSearchParams(queryString);
    Object.entries(extraParams).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        allParams.set(key, value);
      }
    });

    const finalQuery = allParams.toString();
    return finalQuery ? `${url}?${finalQuery}` : url;
  };

  return {
    getConfig: async () => {
      const res = await fetch(buildUrl('/config'), { headers });
      if (!res.ok) throw new Error('Failed to load verification config');
      return res.json();
    },

    getStatus: async () => {
      const res = await fetch(buildUrl('/status'), { headers });
      if (!res.ok) throw new Error('Failed to get status');
      return res.json();
    },

    // Select flow type (for unified KYC/KYB SDK)
    selectFlow: async (entityType) => {
      const res = await fetch(buildUrl('/flow/select'), {
        method: 'POST',
        headers,
        body: JSON.stringify({ entity_type: entityType }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Failed to select flow');
      }
      return res.json();
    },

    getUploadUrl: async (documentType, side, fileName, contentType) => {
      const res = await fetch(buildUrl('/documents/upload-url'), {
        method: 'POST',
        headers,
        body: JSON.stringify({
          document_type: documentType,
          side,
          file_name: fileName,
          content_type: contentType,
        }),
      });
      if (!res.ok) throw new Error('Failed to get upload URL');
      return res.json();
    },

    confirmUpload: async (documentId, fileSize) => {
      const res = await fetch(buildUrl('/documents/confirm'), {
        method: 'POST',
        headers,
        body: JSON.stringify({
          document_id: documentId,
          file_size: fileSize,
        }),
      });
      if (!res.ok) throw new Error('Failed to confirm upload');
      return res.json();
    },

    // Direct upload (bypasses CORS issues with R2)
    uploadDocument: async (file, documentType, side = 'front') => {
      const formData = new FormData();
      formData.append('file', file);

      const res = await fetch(
        buildUrl('/documents/upload', { document_type: documentType, side }),
        {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${accessToken}` },
          body: formData,
        }
      );
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Failed to upload document');
      }
      return res.json();
    },

    completeStep: async (stepName, data = null) => {
      const res = await fetch(buildUrl('/steps/complete'), {
        method: 'POST',
        headers,
        body: JSON.stringify({
          step_name: stepName,
          data,
        }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Failed to complete step');
      }
      return res.json();
    },

    submit: async () => {
      const res = await fetch(buildUrl('/submit'), {
        method: 'POST',
        headers,
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Failed to submit verification');
      }
      return res.json();
    },

    // Address autocomplete using Mapbox
    autocompleteAddress: async (query, country = 'US') => {
      const res = await fetch(buildUrl('/address/autocomplete'), {
        method: 'POST',
        headers,
        body: JSON.stringify({ query, country }),
      });
      if (!res.ok) {
        // Return empty suggestions on error
        return { suggestions: [] };
      }
      return res.json();
    },

    // Address verification
    verifyAddress: async (address) => {
      const res = await fetch(buildUrl('/address/verify'), {
        method: 'POST',
        headers,
        body: JSON.stringify(address),
      });
      if (!res.ok) {
        return { verified: false, verification_status: 'error' };
      }
      return res.json();
    },

    // EDD Questionnaire submission
    submitQuestionnaire: async (answers) => {
      const res = await fetch(buildUrl('/questionnaire/submit'), {
        method: 'POST',
        headers,
        body: JSON.stringify({ answers }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Failed to submit questionnaire');
      }
      return res.json();
    },

    // CDD Questionnaire submission (with risk scoring)
    submitCddQuestionnaire: async (answers, framework, riskScores) => {
      const res = await fetch(buildUrl('/cdd-questionnaire/submit'), {
        method: 'POST',
        headers,
        body: JSON.stringify({ answers, framework, risk_scores: riskScores }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Failed to submit CDD questionnaire');
      }
      return res.json();
    },

    // Liveness check - create session
    createLivenessSession: async (provider = 'aws') => {
      const res = await fetch(buildUrl('/liveness/session', { provider }), {
        method: 'POST',
        headers,
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Failed to create liveness session');
      }
      return res.json();
    },

    // Liveness check - complete (sends rich signal data for ML risk scoring)
    completeLiveness: async (sessionId, confidence, metadata = {}) => {
      const { provider = 'aws', challengesCompleted, ...signals } = metadata;
      const res = await fetch(buildUrl('/liveness/complete'), {
        method: 'POST',
        headers,
        body: JSON.stringify({
          session_id: sessionId,
          confidence,
          provider,
          challenges_completed: challengesCompleted,
          // Rich signal data for ML risk scoring
          anti_spoof_score: signals.anti_spoof_score,
          frames_analyzed: signals.frames_analyzed,
          frame_scores: signals.frame_scores,
          device_info: {
            user_agent: navigator.userAgent,
            platform: navigator.platform,
            screen_width: window.screen.width,
            screen_height: window.screen.height,
            device_pixel_ratio: window.devicePixelRatio,
            timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          },
        }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Liveness verification failed');
      }
      return res.json();
    },

    // Liveness handoff - create handoff token for QR code
    createLivenessHandoff: async () => {
      const res = await fetch(buildUrl('/liveness/handoff'), {
        method: 'POST',
        headers,
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Failed to create handoff session');
      }
      return res.json();
    },

    // Liveness status polling (for desktop QR handoff)
    checkLivenessStatus: async (sessionId) => {
      const res = await fetch(buildUrl('/liveness/status', { session_id: sessionId }), {
        method: 'GET',
        headers,
      });
      if (!res.ok) return null;
      return res.json();
    },

    // KYB-specific endpoints
    // Submit company information
    submitCompanyInfo: async (data) => {
      const res = await fetch(buildUrl('/company/info'), {
        method: 'POST',
        headers,
        body: JSON.stringify(data),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Failed to submit company information');
      }
      return res.json();
    },

    // Submit beneficial owners (UBOs)
    submitUBOs: async (data) => {
      const res = await fetch(buildUrl('/company/ubos'), {
        method: 'POST',
        headers,
        body: JSON.stringify(data),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Failed to submit beneficial owners');
      }
      return res.json();
    },

    // Upload corporate document
    uploadCorporateDocument: async (file, documentType) => {
      const formData = new FormData();
      formData.append('file', file);

      const res = await fetch(
        buildUrl('/company/documents', { document_type: documentType }),
        {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${accessToken}` },
          body: formData,
        }
      );
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Failed to upload corporate document');
      }
      return res.json();
    },

    // Get company verification status
    getCompanyStatus: async () => {
      const res = await fetch(buildUrl('/company/status'), { headers });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Failed to get company status');
      }
      return res.json();
    },
  };
};

// ============================================
// STEP 1: CONSENT
// ============================================
// Neutral consent for white-label tenants: what is collected, why, and the
// tenant's privacy link. No marketing claims. Only used when white_label is on.
const NeutralConsentStep = ({ onComplete, branding, stepConfig }) => {
  const [agreed, setAgreed] = useState(false);
  const [animateIn, setAnimateIn] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setAnimateIn(true), 100);
    return () => clearTimeout(t);
  }, []);

  const companyName = typeof branding?.company_name === 'string' && branding.company_name.trim()
    ? branding.company_name.trim()
    : null;
  const privacyUrl = [branding?.privacy_url, branding?.privacy_policy_url]
    .find((u) => typeof u === 'string' && /^https:\/\//i.test(u));
  const customText = typeof stepConfig?.consent_text === 'string' && stepConfig.consent_text.trim()
    ? stepConfig.consent_text.trim()
    : null;

  return (
    <div className={`sdk-step consent-step neutral-consent ${animateIn ? 'animate-in' : ''}`}>
      <div className="step-header">
        {branding?.logo_url && (
          // White-label tenants supply a full lockup: show it at its own aspect ratio, no icon tile.
          <img src={branding.logo_url} alt={companyName || 'Logo'} className="sdk-lockup-logo" />
        )}
        <h2>Verify your identity</h2>
        <p className="step-subtitle">
          {customText || `${companyName || 'We'} ${companyName ? 'needs' : 'need'} to confirm your identity before you continue.`}
        </p>
      </div>

      <div className="consent-box">
        <div className="consent-header">
          <FileText size={18} />
          <span>What we collect</span>
        </div>
        <ul className="consent-list">
          <li><CheckCircle2 size={16} /> A photo of your government ID</li>
          <li><CheckCircle2 size={16} /> A selfie to match your ID</li>
          <li><CheckCircle2 size={16} /> The details you enter and the details on your ID</li>
        </ul>
        <p className="neutral-consent-why">
          We use this only to verify your identity and to meet legal requirements.
        </p>
        {privacyUrl && (
          <a className="neutral-consent-link" href={privacyUrl} target="_blank" rel="noopener noreferrer">
            Privacy policy
          </a>
        )}
      </div>

      <label className="consent-checkbox">
        <input
          type="checkbox"
          checked={agreed}
          onChange={(e) => setAgreed(e.target.checked)}
        />
        <div className="checkbox-custom">
          {agreed && <CheckCircle2 size={14} />}
        </div>
        <span>I agree to the processing of my data for identity verification</span>
      </label>

      <button
        className="sdk-btn primary full-width"
        disabled={!agreed}
        onClick={() => onComplete({ consent: true })}
      >
        <span>Continue</span>
        <ChevronRight size={18} />
      </button>
    </div>
  );
};

const ConsentStep = ({ onComplete, branding, whiteLabel = false, stepConfig }) => {
  const [agreed, setAgreed] = useState(false);
  const [animateIn, setAnimateIn] = useState(false);

  useEffect(() => {
    setTimeout(() => setAnimateIn(true), 100);
  }, []);

  if (whiteLabel) {
    return <NeutralConsentStep onComplete={onComplete} branding={branding} stepConfig={stepConfig} />;
  }

  const features = [
    { icon: Lock, title: 'Secure & Encrypted', desc: 'Bank-level security protects your data' },
    { icon: Zap, title: 'Fast Verification', desc: 'Complete in under 2 minutes' },
    { icon: Shield, title: 'Privacy First', desc: 'Data used only for verification' },
  ];

  return (
    <div className={`sdk-step consent-step ${animateIn ? 'animate-in' : ''}`}>
      <div className="step-header">
        <div className="step-icon-wrapper">
          <div className="step-icon-bg" />
          {branding?.logo_url ? (
            <img src={branding.logo_url} alt="Logo" className="sdk-tenant-logo" />
          ) : (
            <Shield size={32} className="step-icon" />
          )}
        </div>
        <h2>Verify Your Identity</h2>
        <p className="step-subtitle">
          Quick and secure identity verification to keep everyone safe
        </p>
      </div>

      <div className="features-grid">
        {features.map((feature, idx) => (
          <div key={idx} className="feature-card" style={{ animationDelay: `${idx * 100}ms` }}>
            <feature.icon size={20} />
            <div>
              <div className="feature-title">{feature.title}</div>
              <div className="feature-desc">{feature.desc}</div>
            </div>
          </div>
        ))}
      </div>

      <div className="consent-box">
        <div className="consent-header">
          <FileText size={18} />
          <span>What we'll collect</span>
        </div>
        <ul className="consent-list">
          <li><CheckCircle2 size={16} /> Photo of your government ID</li>
          <li><CheckCircle2 size={16} /> A selfie to match your ID</li>
          <li><CheckCircle2 size={16} /> Basic information from your ID</li>
        </ul>
      </div>

      <label className="consent-checkbox">
        <input
          type="checkbox"
          checked={agreed}
          onChange={(e) => setAgreed(e.target.checked)}
        />
        <div className="checkbox-custom">
          {agreed && <CheckCircle2 size={14} />}
        </div>
        <span>I agree to the processing of my data for identity verification</span>
      </label>

      <button
        className="sdk-btn primary full-width"
        disabled={!agreed}
        onClick={() => onComplete({ consent: true })}
      >
        <span>Get Started</span>
        <ChevronRight size={18} />
      </button>

      <p className="secure-notice">
        <Lock size={12} />
        Your information is encrypted and secure
      </p>
    </div>
  );
};

// ============================================
// STEP 2: PERSONAL INFORMATION
// ============================================

// Phone number formatting helper
const formatPhoneNumber = (value) => {
  // Remove all non-digits
  const digits = value.replace(/\D/g, '');

  // Format as US phone number
  if (digits.length === 0) return '';
  if (digits.length <= 3) return `(${digits}`;
  if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  if (digits.length <= 10) return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  // Handle country code
  if (digits.length > 10) {
    const countryCode = digits.slice(0, digits.length - 10);
    const rest = digits.slice(-10);
    return `+${countryCode} (${rest.slice(0, 3)}) ${rest.slice(3, 6)}-${rest.slice(6)}`;
  }
  return value;
};

// Modern Date Picker Component (Stripe-style)
const DatePicker = ({ value, onChange, error, minAge = 18 }) => {
  const [month, setMonth] = useState('');
  const [day, setDay] = useState('');
  const [year, setYear] = useState('');

  // Parse initial value
  useEffect(() => {
    if (value) {
      const [y, m, d] = value.split('-');
      setYear(y || '');
      setMonth(m || '');
      setDay(d || '');
    }
  }, [value]);

  // Update parent when all fields are filled
  useEffect(() => {
    if (month && day && year && year.length === 4) {
      const dateStr = `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
      onChange(dateStr);
    }
  }, [month, day, year, onChange]);

  const currentYear = new Date().getFullYear();
  const maxYear = currentYear - minAge;
  const minYear = currentYear - 120;

  const months = [
    { value: '01', label: 'January' },
    { value: '02', label: 'February' },
    { value: '03', label: 'March' },
    { value: '04', label: 'April' },
    { value: '05', label: 'May' },
    { value: '06', label: 'June' },
    { value: '07', label: 'July' },
    { value: '08', label: 'August' },
    { value: '09', label: 'September' },
    { value: '10', label: 'October' },
    { value: '11', label: 'November' },
    { value: '12', label: 'December' },
  ];

  const getDaysInMonth = (m, y) => {
    if (!m) return 31;
    return new Date(y || 2000, parseInt(m), 0).getDate();
  };

  const days = Array.from({ length: getDaysInMonth(month, year) }, (_, i) =>
    String(i + 1).padStart(2, '0')
  );

  // Generate years from maxYear down to minYear
  const years = Array.from({ length: maxYear - minYear + 1 }, (_, i) =>
    String(maxYear - i)
  );

  return (
    <div className={`date-picker-modern ${error ? 'has-error' : ''}`}>
      <div className="date-picker-row">
        <select
          value={month}
          onChange={(e) => setMonth(e.target.value)}
          className="date-select month-select"
        >
          <option value="">Month</option>
          {months.map(m => (
            <option key={m.value} value={m.value}>{m.label}</option>
          ))}
        </select>
        <select
          value={day}
          onChange={(e) => setDay(e.target.value)}
          className="date-select day-select"
        >
          <option value="">Day</option>
          {days.map(d => (
            <option key={d} value={d}>{parseInt(d)}</option>
          ))}
        </select>
        <select
          value={year}
          onChange={(e) => setYear(e.target.value)}
          className="date-select year-select"
        >
          <option value="">Year</option>
          {years.map(y => (
            <option key={y} value={y}>{y}</option>
          ))}
        </select>
      </div>
    </div>
  );
};

// Address Autocomplete Component using Mapbox API
const AddressAutocomplete = ({ value, onChange, onSelect, client }) => {
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [loading, setLoading] = useState(false);
  const inputRef = useRef(null);
  const debounceRef = useRef(null);

  // Fetch address suggestions from Mapbox via backend API
  const fetchSuggestions = async (query) => {
    if (query.length < 4) {
      setSuggestions([]);
      return;
    }

    setLoading(true);

    try {
      // Call backend address autocomplete endpoint
      const response = await client.autocompleteAddress(query);

      if (response.suggestions && response.suggestions.length > 0) {
        setSuggestions(response.suggestions.map((s, idx) => ({
          id: idx,
          street_line_1: s.street_line_1,
          city: s.city,
          state: s.state,
          postal_code: s.postal_code,
          country: s.country,
          full_address: s.full_address,
        })));
        // Reveal the dropdown as soon as results arrive. Without this the
        // dropdown only showed on a later focus (when suggestions already
        // existed), so typing an address never surfaced any suggestions.
        setShowSuggestions(true);
      } else {
        setSuggestions([]);
        setShowSuggestions(false);
      }
    } catch (error) {
      console.error('Address autocomplete error:', error);
      setSuggestions([]);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const val = e.target.value;
    onChange(val);

    // Debounce API calls
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchSuggestions(val), 350);
  };

  const handleSelect = (suggestion) => {
    onChange(suggestion.street_line_1);
    setShowSuggestions(false);

    // Pass structured address data to parent
    onSelect({
      street: suggestion.street_line_1,
      city: suggestion.city,
      state: suggestion.state,
      postal_code: suggestion.postal_code,
      country: suggestion.country,
    });
  };

  return (
    <div className="address-autocomplete">
      <input
        ref={inputRef}
        type="text"
        value={value}
        onChange={handleInputChange}
        onFocus={() => suggestions.length > 0 && setShowSuggestions(true)}
        onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
        placeholder="Start typing your address..."
        autoComplete="off"
      />
      {loading && <div className="autocomplete-loading"><Loader2 size={14} className="spin" /></div>}
      {showSuggestions && suggestions.length > 0 && (
        <div className="autocomplete-dropdown">
          {suggestions.map((s) => (
            <div
              key={s.id}
              className="autocomplete-item"
              onClick={() => handleSelect(s)}
            >
              <MapPin size={14} />
              <span>{s.full_address}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Normalize host prefill into the form shape. Accepts either {street} or
// {line1, line2} addresses and ISO country codes. Every value stays editable.
const PREFILL_COUNTRY_NAMES = {
  US: 'United States', USA: 'United States', CA: 'Canada', CAN: 'Canada',
  GB: 'United Kingdom', GBR: 'United Kingdom', UK: 'United Kingdom', AU: 'Australia', AUS: 'Australia',
};
const asText = (v) => (typeof v === 'string' ? v.trim() : '');
const normalizePrefill = (prefill) => {
  if (!prefill || typeof prefill !== 'object') return { address: {} };
  const addr = prefill.address && typeof prefill.address === 'object' ? prefill.address : {};
  const street = asText(addr.street) || [asText(addr.line1), asText(addr.line2)].filter(Boolean).join(', ');
  const countryRaw = asText(addr.country);
  const country = PREFILL_COUNTRY_NAMES[countryRaw.toUpperCase()] || countryRaw;
  const address = {};
  if (street) address.street = street;
  if (asText(addr.city)) address.city = asText(addr.city);
  if (asText(addr.state)) address.state = asText(addr.state);
  const postal = asText(addr.postal_code) || asText(addr.postal) || asText(addr.zip);
  if (postal) address.postal_code = postal;
  if (country) address.country = country;
  return {
    first_name: asText(prefill.first_name),
    last_name: asText(prefill.last_name),
    date_of_birth: asText(prefill.date_of_birth),
    email: asText(prefill.email),
    phone: asText(prefill.phone),
    address,
  };
};

const PersonalInfoStep = ({ onComplete, onBack, client, stepConfig, fieldConfigEnabled = false, prefill = null }) => {
  // Builder field config is honored only when the caller opts in (embed mode or
  // white-label). Otherwise every field renders exactly as before.
  const fields = fieldConfigEnabled && Array.isArray(stepConfig?.fields) && stepConfig.fields.length > 0
    ? stepConfig.fields
    : null;
  const show = (name) => !fields || fields.includes(name);

  const [formData, setFormData] = useState(() => {
    const base = {
      first_name: '',
      last_name: '',
      date_of_birth: '',
      nationality: '',
      email: '',
      phone: '',
      address: {
        street: '',
        city: '',
        state: '',
        postal_code: '',
        country: '',
      },
    };
    if (!fieldConfigEnabled || !prefill) return base;
    const pre = normalizePrefill(prefill);
    return {
      ...base,
      first_name: pre.first_name || '',
      last_name: pre.last_name || '',
      // DOB is only prefilled when the host provided it explicitly.
      date_of_birth: pre.date_of_birth || '',
      email: pre.email || '',
      phone: pre.phone || '',
      address: { ...base.address, ...pre.address },
    };
  });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [animateIn, setAnimateIn] = useState(false);
  useReportStepError('personal_info', errors.submit);

  useEffect(() => {
    setTimeout(() => setAnimateIn(true), 100);
  }, []);

  const handleChange = (field, value) => {
    if (field.includes('.')) {
      const [parent, child] = field.split('.');
      setFormData(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value,
        },
      }));
    } else {
      setFormData(prev => ({ ...prev, [field]: value }));
    }
    // Clear error on change
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  const handlePhoneChange = (e) => {
    const formatted = formatPhoneNumber(e.target.value);
    handleChange('phone', formatted);
  };

  const handleAddressSelect = (addressData) => {
    setFormData(prev => ({
      ...prev,
      address: { ...prev.address, ...addressData },
    }));
  };

  const validateForm = () => {
    const newErrors = {};

    if (show('first_name') && !formData.first_name.trim()) {
      newErrors.first_name = 'First name is required';
    }
    if (show('last_name') && !formData.last_name.trim()) {
      newErrors.last_name = 'Last name is required';
    }
    if (show('date_of_birth')) {
      if (!formData.date_of_birth) {
        newErrors.date_of_birth = 'Date of birth is required';
      } else {
        const dob = new Date(formData.date_of_birth);
        const age = (new Date() - dob) / (365.25 * 24 * 60 * 60 * 1000);
        if (age < 18) {
          newErrors.date_of_birth = 'You must be at least 18 years old';
        }
      }
    }
    if (show('nationality') && !formData.nationality.trim()) {
      newErrors.nationality = 'Nationality is required';
    }
    if (show('email') && formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Invalid email format';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setSubmitting(true);
    try {
      // With a field list, send only the fields the user saw so hidden fields
      // never overwrite applicant data with empty values.
      const payload = fields
        ? Object.fromEntries(Object.entries(formData).filter(([key]) => fields.includes(key)))
        : formData;
      await onComplete(payload);
    } catch (err) {
      setErrors({ submit: err.message || 'Failed to save information' });
    } finally {
      setSubmitting(false);
    }
  };

  const countries = [
    'United States', 'Canada', 'United Kingdom', 'Australia', 'Germany',
    'France', 'Spain', 'Italy', 'Netherlands', 'Switzerland', 'Sweden',
    'Norway', 'Denmark', 'Japan', 'South Korea', 'China', 'India',
    'Singapore', 'Brazil', 'Mexico', 'Argentina', 'Other'
  ];

  return (
    <div className={`sdk-step personal-info-step ${animateIn ? 'animate-in' : ''}`}>
      <div className="step-header">
        <div className="step-icon-wrapper">
          <div className="step-icon-bg" />
          <UserCircle size={32} className="step-icon" />
        </div>
        <h2>Your Information</h2>
        <p className="step-subtitle">
          Please provide your personal details for verification
        </p>
      </div>

      <div className="personal-info-form">
        {/* Name Row */}
        <div className="form-row two-col">
          {show('first_name') && (
            <div className="form-group">
              <label>
                <User size={14} />
                First Name *
              </label>
              <input
                type="text"
                value={formData.first_name}
                onChange={(e) => handleChange('first_name', e.target.value)}
                placeholder="John"
                className={errors.first_name ? 'error' : ''}
              />
              {errors.first_name && <span className="field-error">{errors.first_name}</span>}
            </div>
          )}
          {show('last_name') && (
            <div className="form-group">
              <label>
                <User size={14} />
                Last Name *
              </label>
              <input
                type="text"
                value={formData.last_name}
                onChange={(e) => handleChange('last_name', e.target.value)}
                placeholder="Smith"
                className={errors.last_name ? 'error' : ''}
              />
              {errors.last_name && <span className="field-error">{errors.last_name}</span>}
            </div>
          )}
        </div>

        {/* DOB Row - Full Width */}
        {show('date_of_birth') && (
          <div className="form-group">
            <label>
              <Calendar size={14} />
              Date of Birth *
            </label>
            <DatePicker
              value={formData.date_of_birth}
              onChange={(val) => handleChange('date_of_birth', val)}
              error={errors.date_of_birth}
            />
            {errors.date_of_birth && <span className="field-error">{errors.date_of_birth}</span>}
          </div>
        )}

        {/* Nationality Row - Full Width */}
        {show('nationality') && (
          <div className="form-group">
            <label>
              <Globe size={14} />
              Nationality *
            </label>
            <select
              value={formData.nationality}
              onChange={(e) => handleChange('nationality', e.target.value)}
              className={errors.nationality ? 'error' : ''}
            >
              <option value="">Select country</option>
              {countries.map(c => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
            {errors.nationality && <span className="field-error">{errors.nationality}</span>}
          </div>
        )}

        {/* Contact Row */}
        {(show('email') || show('phone')) && (
          <div className="form-row two-col">
            {show('email') && (
              <div className="form-group">
                <label>
                  <Mail size={14} />
                  Email
                </label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleChange('email', e.target.value)}
                  placeholder="john@example.com"
                  className={errors.email ? 'error' : ''}
                />
                {errors.email && <span className="field-error">{errors.email}</span>}
              </div>
            )}
            {show('phone') && (
              <div className="form-group">
                <label>
                  <Phone size={14} />
                  Phone
                </label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={handlePhoneChange}
                  placeholder="(555) 123-4567"
                />
              </div>
            )}
          </div>
        )}

        {/* Address Section */}
        {show('address') && (
          <div className="form-section">
            <div className="section-header">
              <MapPin size={16} />
              <span>Address (Optional)</span>
            </div>

            <div className="form-group">
              <label>Street Address</label>
              <AddressAutocomplete
                value={formData.address.street}
                onChange={(val) => handleChange('address.street', val)}
                onSelect={handleAddressSelect}
                client={client}
              />
            </div>

            <div className="form-row two-col">
              <div className="form-group">
                <label>City</label>
                <input
                  type="text"
                  value={formData.address.city}
                  onChange={(e) => handleChange('address.city', e.target.value)}
                  placeholder="New York"
                />
              </div>
              <div className="form-group">
                <label>State / Province</label>
                <input
                  type="text"
                  value={formData.address.state}
                  onChange={(e) => handleChange('address.state', e.target.value)}
                  placeholder="NY"
                />
              </div>
            </div>

            <div className="form-row two-col">
              <div className="form-group">
                <label>Postal Code</label>
                <input
                  type="text"
                  value={formData.address.postal_code}
                  onChange={(e) => handleChange('address.postal_code', e.target.value)}
                  placeholder="10001"
                />
              </div>
              <div className="form-group">
                <label>Country</label>
                <select
                  value={formData.address.country}
                  onChange={(e) => handleChange('address.country', e.target.value)}
                >
                  <option value="">Select country</option>
                  {countries.map(c => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        )}
      </div>

      {errors.submit && (
        <div className="error-message">
          <AlertCircle size={16} />
          {errors.submit}
        </div>
      )}

      <button
        className="sdk-btn primary full-width"
        disabled={submitting}
        onClick={handleSubmit}
      >
        {submitting ? (
          <>
            <Loader2 size={18} className="spin" />
            Saving...
          </>
        ) : (
          <>
            <span>Continue</span>
            <ChevronRight size={18} />
          </>
        )}
      </button>

      {onBack && (
        <button className="back-button" onClick={onBack}>
          ← Back to Consent
        </button>
      )}
    </div>
  );
};

// ============================================
// STEP: STANDALONE ADDRESS
// ============================================
const AddressStep = ({ onComplete, onBack, client, stepConfig, fieldConfigEnabled = false, prefill = null }) => {
  // Builder field config is honored only when the caller opts in (embed mode or
  // white-label). Field names: street, city, state, postal_code, country.
  const fields = fieldConfigEnabled && Array.isArray(stepConfig?.fields) && stepConfig.fields.length > 0
    ? stepConfig.fields
    : null;
  const show = (name) => !fields || fields.includes(name);
  // Second address line: only when the builder field config lists it.
  const hasLine2 = !!fields && (fields.includes('line2') || fields.includes('address_line2'));

  const [address, setAddress] = useState(() => {
    const base = {
      street: '',
      city: '',
      state: '',
      postal_code: '',
      country: 'United States',
      ...(hasLine2 ? { line2: '' } : {}),
    };
    if (!fieldConfigEnabled || !prefill) return base;
    const merged = { ...base, ...normalizePrefill(prefill).address };
    if (hasLine2) {
      // Keep line1 and line2 apart instead of joining them into street.
      const addr = prefill.address && typeof prefill.address === 'object' ? prefill.address : {};
      const line1 = asText(addr.street) || asText(addr.line1);
      const line2 = asText(addr.line2) || asText(addr.address_line2);
      if (line1) merged.street = line1;
      if (line2) merged.line2 = line2;
    }
    return merged;
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [animateIn, setAnimateIn] = useState(false);
  useReportStepError('address', error);

  useEffect(() => {
    setTimeout(() => setAnimateIn(true), 100);
  }, []);

  const required = stepConfig?.is_required !== false;

  const countries = [
    'United States', 'Canada', 'United Kingdom', 'Australia', 'Germany',
    'France', 'Spain', 'Italy', 'Netherlands', 'Switzerland', 'Sweden',
    'Norway', 'Denmark', 'Japan', 'South Korea', 'China', 'India',
    'Singapore', 'Brazil', 'Mexico', 'Argentina', 'Other'
  ];

  const update = (field, value) => {
    setAddress(prev => ({ ...prev, [field]: value }));
    if (error) setError(null);
  };

  const handleAutocompleteSelect = (data) => {
    setAddress(prev => ({ ...prev, ...data }));
  };

  const handleSubmit = async () => {
    if (required && show('street') && !address.street.trim()) {
      setError('Please enter your street address.');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const payload = fields
        ? Object.fromEntries(Object.entries(address).filter(([key]) => (
          key === 'line2' ? hasLine2 && address.line2.trim() !== '' : fields.includes(key)
        )))
        : address;
      await onComplete(payload);
    } catch (err) {
      setError(err.message || 'Failed to save address. Please try again.');
      setSubmitting(false);
    }
  };

  return (
    <div className={`sdk-step address-step ${animateIn ? 'animate-in' : ''}`}>
      <div className="step-header">
        <div className="step-icon-wrapper">
          <div className="step-icon-bg" />
          <MapPin size={32} className="step-icon" />
        </div>
        <h2>{stepConfig?.title || 'Your Address'}</h2>
        <p className="step-subtitle">
          {stepConfig?.description || 'Enter and verify your residential address.'}
        </p>
      </div>

      <div className="personal-info-form">
        {show('street') && (
          <div className="form-group">
            <label>
              <MapPin size={14} />
              Street Address{required ? ' *' : ''}
            </label>
            <AddressAutocomplete
              value={address.street}
              onChange={(val) => update('street', val)}
              onSelect={handleAutocompleteSelect}
              client={client}
            />
          </div>
        )}

        {hasLine2 && (
          <div className="form-group">
            <label>Apartment, suite, unit (optional)</label>
            <input
              type="text"
              value={address.line2}
              onChange={(e) => update('line2', e.target.value)}
              placeholder="Apt 4B"
              autoComplete="address-line2"
            />
          </div>
        )}

        {(show('city') || show('state')) && (
          <div className="form-row two-col">
            {show('city') && (
              <div className="form-group">
                <label>City</label>
                <input
                  type="text"
                  value={address.city}
                  onChange={(e) => update('city', e.target.value)}
                  placeholder="New York"
                />
              </div>
            )}
            {show('state') && (
              <div className="form-group">
                <label>State / Province</label>
                <input
                  type="text"
                  value={address.state}
                  onChange={(e) => update('state', e.target.value)}
                  placeholder="NY"
                />
              </div>
            )}
          </div>
        )}

        {(show('postal_code') || show('country')) && (
          <div className="form-row two-col">
            {show('postal_code') && (
              <div className="form-group">
                <label>Postal Code</label>
                <input
                  type="text"
                  value={address.postal_code}
                  onChange={(e) => update('postal_code', e.target.value)}
                  placeholder="10001"
                />
              </div>
            )}
            {show('country') && (
              <div className="form-group">
                <label>Country</label>
                <select
                  value={address.country}
                  onChange={(e) => update('country', e.target.value)}
                >
                  <option value="">Select country</option>
                  {countries.map(c => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
              </div>
            )}
          </div>
        )}
      </div>

      {error && (
        <div className="error-message">
          <AlertCircle size={16} />
          {error}
        </div>
      )}

      <button
        className="sdk-btn primary full-width"
        disabled={submitting}
        onClick={handleSubmit}
      >
        {submitting ? (
          <>
            <Loader2 size={18} className="spin" />
            Saving...
          </>
        ) : (
          <>
            <span>Continue</span>
            <ChevronRight size={18} />
          </>
        )}
      </button>

      {onBack && (
        <button className="back-button" onClick={onBack}>
          ← Back
        </button>
      )}
    </div>
  );
};

// ============================================
// STEP 3: DOCUMENT UPLOAD
// ============================================
const DocumentStep = ({ onComplete, onBack, client }) => {
  const [documentType, setDocumentType] = useState(null);
  const [currentSide, setCurrentSide] = useState('front');
  const [frontFile, setFrontFile] = useState(null);
  const [backFile, setBackFile] = useState(null);
  const [frontPreview, setFrontPreview] = useState(null);
  const [backPreview, setBackPreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [processingMessage, setProcessingMessage] = useState('');
  const [error, setError] = useState(null);
  const [cameraMode, setCameraMode] = useState(false);
  const [animateIn, setAnimateIn] = useState(false);
  const embed = useSDKEmbed();
  useReportStepError('document', error);

  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    setTimeout(() => setAnimateIn(true), 100);
  }, []);

  const documentTypes = [
    {
      value: 'passport',
      label: 'Passport',
      icon: Globe,
      desc: 'International travel document',
      requiresBack: false
    },
    {
      value: 'driver_license',
      label: "Driver's License",
      icon: Car,
      desc: 'State or national license',
      requiresBack: true
    },
    {
      value: 'id_card',
      label: 'ID Card',
      icon: CreditCard,
      desc: 'National identity card',
      requiresBack: true
    },
  ];

  const selectedDoc = documentTypes.find(d => d.value === documentType);
  const requiresBack = selectedDoc?.requiresBack ?? false;
  const needsBackPhoto = requiresBack && currentSide === 'front' && frontFile && !backFile;

  const startCamera = async () => {
    // First enable camera mode to mount the video element
    setCameraMode(true);
    setError(null);
  };

  // Effect to initialize camera stream when cameraMode becomes true
  useEffect(() => {
    if (!cameraMode) return;

    const initCamera = async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: {
            facingMode: 'environment',
            width: { ideal: 1920 },
            height: { ideal: 1080 },
          },
        });
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          await videoRef.current.play();
        }
      } catch (err) {
        console.error('Camera error:', err);
        setError('Unable to access camera. Please upload a photo instead.');
        setCameraMode(false);
      }
    };

    // Small delay to ensure video element is mounted
    const timer = setTimeout(initCamera, 100);
    return () => clearTimeout(timer);
  }, [cameraMode]);

  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    setCameraMode(false);
  };

  const capturePhoto = () => {
    if (!videoRef.current || !canvasRef.current) return;

    const video = videoRef.current;
    const canvas = canvasRef.current;
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;

    const ctx = canvas.getContext('2d');
    ctx.drawImage(video, 0, 0);

    canvas.toBlob((blob) => {
      if (!blob) {
        setError('Failed to capture photo. Please try again.');
        return;
      }
      const file = new File([blob], `${documentType}_${currentSide}.jpg`, { type: 'image/jpeg' });
      const preview = URL.createObjectURL(blob);

      if (currentSide === 'front') {
        if (frontPreview) URL.revokeObjectURL(frontPreview);
        setFrontFile(file);
        setFrontPreview(preview);
      } else {
        if (backPreview) URL.revokeObjectURL(backPreview);
        setBackFile(file);
        setBackPreview(preview);
      }
      stopCamera();
    }, 'image/jpeg', 0.92);
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/') && file.type !== 'application/pdf') {
      setError('Please upload an image or PDF file');
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      setError('File size must be less than 10MB');
      return;
    }

    setError(null);
    const preview = URL.createObjectURL(file);

    if (currentSide === 'front') {
      if (frontPreview) URL.revokeObjectURL(frontPreview);
      setFrontFile(file);
      setFrontPreview(preview);
    } else {
      if (backPreview) URL.revokeObjectURL(backPreview);
      setBackFile(file);
      setBackPreview(preview);
    }
  };

  const clearPhoto = (side) => {
    if (side === 'front') {
      if (frontPreview) URL.revokeObjectURL(frontPreview);
      setFrontFile(null);
      setFrontPreview(null);
    } else {
      if (backPreview) URL.revokeObjectURL(backPreview);
      setBackFile(null);
      setBackPreview(null);
    }
  };

  const handleContinue = async () => {
    // If we need back and don't have it yet, switch to back
    if (needsBackPhoto) {
      setCurrentSide('back');
      return;
    }

    // Upload documents
    setUploading(true);
    setProcessing(true);
    setError(null);

    try {
      // Resize images before upload (max 3000px, preserves OCR quality)
      setProcessingMessage('Preparing document...');
      const resizedFront = await resizeImageIfNeeded(frontFile);

      // Upload front (direct to backend, bypasses CORS)
      setProcessingMessage('Uploading front of document...');
      await client.uploadDocument(resizedFront, documentType, 'front');

      // Upload back if needed
      if (requiresBack && backFile) {
        const resizedBack = await resizeImageIfNeeded(backFile);
        setProcessingMessage('Uploading back of document...');
        await client.uploadDocument(resizedBack, documentType, 'back');
      }

      setProcessingMessage('Analyzing document...');
      await new Promise(r => setTimeout(r, 800)); // Brief delay for UX

      await onComplete({
        document_type: documentType,
        front_uploaded: true,
        back_uploaded: requiresBack && !!backFile,
      });

    } catch (err) {
      setError(err.message || 'Upload failed. Please try again.');
    } finally {
      setUploading(false);
      setProcessing(false);
      setProcessingMessage('');
    }
  };

  /* eslint-disable react-hooks/exhaustive-deps */
  // Cleanup on unmount only - intentionally empty deps
  useEffect(() => {
    return () => {
      stopCamera();
      if (frontPreview) URL.revokeObjectURL(frontPreview);
      if (backPreview) URL.revokeObjectURL(backPreview);
    };
  }, []);
  /* eslint-enable react-hooks/exhaustive-deps */

  // Document type selection
  if (!documentType) {
    return (
      <div className={`sdk-step document-step ${animateIn ? 'animate-in' : ''}`}>
        <div className="step-header">
          <div className="step-icon-wrapper">
            <div className="step-icon-bg" />
            <FileText size={32} className="step-icon" />
          </div>
          <h2>Select ID Type</h2>
          <p className="step-subtitle">
            Choose the type of government-issued ID you'll use
          </p>
        </div>

        <div className="document-type-grid">
          {documentTypes.map((type, idx) => (
            <button
              key={type.value}
              className="document-type-card"
              onClick={() => setDocumentType(type.value)}
              style={{ animationDelay: `${idx * 100}ms` }}
            >
              <div className="doc-type-icon">
                <type.icon size={28} />
              </div>
              <div className="doc-type-info">
                <div className="doc-type-label">{type.label}</div>
                <div className="doc-type-desc">{type.desc}</div>
              </div>
              <ChevronRight size={18} className="doc-type-arrow" />
            </button>
          ))}
        </div>

        {onBack && (
          <button className="back-button" onClick={onBack}>
            ← Back to Consent
          </button>
        )}
      </div>
    );
  }

  // Camera / capture view
  const currentPreview = currentSide === 'front' ? frontPreview : backPreview;
  const hasCurrentPhoto = currentSide === 'front' ? !!frontFile : !!backFile;

  return (
    <div className={`sdk-step document-step ${animateIn ? 'animate-in' : ''}`}>
      <div className="step-header compact">
        <h2>
          {selectedDoc?.label} - {currentSide === 'front' ? 'Front' : 'Back'}
        </h2>
        <p className="step-subtitle">
          {currentSide === 'front'
            ? 'Take a clear photo of the front of your ID'
            : 'Now capture the back of your ID'}
        </p>
      </div>

      {/* Progress dots for front/back */}
      {requiresBack && (
        <div className="capture-progress">
          <div className={`capture-dot ${frontFile ? 'done' : currentSide === 'front' ? 'active' : ''}`}>
            {frontFile ? <CheckCircle2 size={14} /> : '1'}
          </div>
          <div className="capture-line" />
          <div className={`capture-dot ${backFile ? 'done' : currentSide === 'back' ? 'active' : ''}`}>
            {backFile ? <CheckCircle2 size={14} /> : '2'}
          </div>
        </div>
      )}

      <div className="capture-area">
        {cameraMode ? (
          <div className="camera-container document-camera">
            <video ref={videoRef} autoPlay playsInline muted />
            <div className="document-frame">
              <div className="frame-corner tl" />
              <div className="frame-corner tr" />
              <div className="frame-corner bl" />
              <div className="frame-corner br" />
            </div>
            <div className="camera-guidance">
              <Sparkles size={16} />
              Position ID within the frame
            </div>
            <div className="camera-controls">
              <button className="camera-btn cancel" onClick={stopCamera}>
                <X size={20} />
              </button>
              <button className="camera-btn capture" onClick={capturePhoto}>
                <div className="capture-ring" />
              </button>
              <div className="camera-btn placeholder" />
            </div>
          </div>
        ) : currentPreview ? (
          <div className="preview-container">
            <img src={currentPreview} alt={`${currentSide} of ID`} />
            <div className="preview-overlay">
              <button className="retake-btn" onClick={() => clearPhoto(currentSide)}>
                <RefreshCw size={16} />
                Retake Photo
              </button>
            </div>
            <div className="preview-badge success">
              <CheckCircle2 size={14} />
              Photo captured
            </div>
          </div>
        ) : (
          <div className="capture-options">
            <button className="capture-option primary" onClick={startCamera}>
              <Camera size={24} />
              <span>Take Photo</span>
              <small>Use your camera</small>
            </button>
            <button className="capture-option" onClick={() => fileInputRef.current?.click()}>
              <Upload size={24} />
              <span>Upload File</span>
              <small>From your device</small>
            </button>
          </div>
        )}

        <input
          ref={fileInputRef}
          type="file"
          accept="image/*,application/pdf"
          onChange={handleFileSelect}
          style={{ display: 'none' }}
        />
        <canvas ref={canvasRef} style={{ display: 'none' }} />
      </div>

      {/* Tips */}
      <div className="capture-tips">
        <div className="tip"><Sun size={14} /> Good lighting</div>
        <div className="tip"><Eye size={14} /> All text visible</div>
        <div className="tip"><Smartphone size={14} /> Flat surface</div>
      </div>

      {error && (
        <div className="error-message">
          <AlertCircle size={16} />
          {error}
        </div>
      )}

      {embed && error && !uploading && (
        <button
          type="button"
          className="sdk-btn secondary full-width embed-retry"
          onClick={() => {
            clearPhoto('front');
            clearPhoto('back');
            setCurrentSide('front');
            setError(null);
          }}
        >
          <RefreshCw size={16} />
          <span>Try again</span>
        </button>
      )}

      {processing && (
        <div className="processing-overlay">
          <Loader2 size={32} className="spin" />
          <span>{processingMessage}</span>
        </div>
      )}

      {hasCurrentPhoto && !cameraMode && (
        <button
          className="sdk-btn primary full-width"
          disabled={uploading}
          onClick={handleContinue}
        >
          {uploading ? (
            <>
              <Loader2 size={18} className="spin" />
              Processing...
            </>
          ) : needsBackPhoto ? (
            <>
              <span>Continue to Back</span>
              <ChevronRight size={18} />
            </>
          ) : (
            <>
              <span>Continue</span>
              <ChevronRight size={18} />
            </>
          )}
        </button>
      )}
    </div>
  );
};

// ============================================
// STEP 4: SELFIE
// ============================================
const SelfieStep = ({ onComplete, onBack, client }) => {
  const [selfieFile, setSelfieFile] = useState(null);
  const [selfiePreview, setSelfiePreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const [cameraMode, setCameraMode] = useState(false);
  const [guidance, setGuidance] = useState('Position your face in the oval');
  const [countdown, setCountdown] = useState(null);
  const [animateIn, setAnimateIn] = useState(false);
  const embed = useSDKEmbed();
  useReportStepError('selfie', error);

  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const fileInputRef = useRef(null);
  const streamRef = useRef(null);

  useEffect(() => {
    setTimeout(() => setAnimateIn(true), 100);
  }, []);

  const startCamera = async () => {
    // First enable camera mode to mount the video element
    setCameraMode(true);
    setGuidance('Position your face in the oval');
    setError(null);
  };

  // Effect to initialize camera stream when cameraMode becomes true
  useEffect(() => {
    if (!cameraMode) return;

    const initCamera = async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: {
            facingMode: 'user',
            width: { ideal: 1280 },
            height: { ideal: 720 },
          },
        });
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          await videoRef.current.play();
        }

        // Simulate face detection feedback
        setTimeout(() => setGuidance('Hold still...'), 2000);
        setTimeout(() => setGuidance('Perfect! Hold that position'), 3500);
      } catch (err) {
        console.error('Camera error:', err);
        setError('Unable to access camera. Please upload a photo instead.');
        setCameraMode(false);
      }
    };

    // Small delay to ensure video element is mounted
    const timer = setTimeout(initCamera, 100);
    return () => clearTimeout(timer);
  }, [cameraMode]);

  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    setCameraMode(false);
    setCountdown(null);
  };

  const initiateCapture = () => {
    setCountdown(3);
    const timer = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) {
          clearInterval(timer);
          capturePhoto();
          return null;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const capturePhoto = () => {
    if (!videoRef.current || !canvasRef.current) return;

    const video = videoRef.current;
    const canvas = canvasRef.current;
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;

    const ctx = canvas.getContext('2d');
    // Mirror the image for selfie
    ctx.translate(canvas.width, 0);
    ctx.scale(-1, 1);
    ctx.drawImage(video, 0, 0);

    canvas.toBlob((blob) => {
      if (!blob) {
        setError('Failed to capture selfie. Please try again.');
        return;
      }
      const file = new File([blob], 'selfie.jpg', { type: 'image/jpeg' });
      setSelfieFile(file);
      setSelfiePreview(URL.createObjectURL(blob));
      stopCamera();
    }, 'image/jpeg', 0.92);
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      setError('Please select an image file');
      return;
    }

    setError(null);
    setSelfieFile(file);
    setSelfiePreview(URL.createObjectURL(file));
  };

  const handleUpload = async () => {
    if (!selfieFile) return;

    setUploading(true);
    setError(null);

    try {
      // Direct upload to backend (bypasses CORS)
      await client.uploadDocument(selfieFile, 'selfie', 'front');
      await onComplete({ selfie_uploaded: true });

    } catch (err) {
      setError(err.message || 'Upload failed. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const clearSelfie = () => {
    if (selfiePreview) URL.revokeObjectURL(selfiePreview);
    setSelfieFile(null);
    setSelfiePreview(null);
  };

  useEffect(() => {
    return () => stopCamera();
  }, []);

  return (
    <div className={`sdk-step selfie-step ${animateIn ? 'animate-in' : ''}`}>
      <div className="step-header compact">
        <h2>Take a Selfie</h2>
        <p className="step-subtitle">
          We'll match this with your ID photo
        </p>
      </div>

      <div className="capture-area selfie-area">
        {cameraMode ? (
          <div className="camera-container selfie-camera">
            <video ref={videoRef} autoPlay playsInline muted />
            <div className="face-oval">
              <div className="oval-border" />
            </div>
            {countdown && (
              <div className="countdown-overlay">
                <div className="countdown-number">{countdown}</div>
              </div>
            )}
            <div className="camera-guidance">
              <Sparkles size={16} />
              {guidance}
            </div>
            <div className="camera-controls">
              <button className="camera-btn cancel" onClick={stopCamera}>
                <X size={20} />
              </button>
              <button className="camera-btn capture" onClick={initiateCapture} disabled={countdown !== null}>
                <div className="capture-ring" />
              </button>
              <div className="camera-btn placeholder" />
            </div>
          </div>
        ) : selfiePreview ? (
          <div className="preview-container selfie-preview">
            <img src={selfiePreview} alt="Your selfie" />
            <div className="preview-overlay">
              <button className="retake-btn" onClick={clearSelfie}>
                <RefreshCw size={16} />
                Retake Selfie
              </button>
            </div>
            <div className="preview-badge success">
              <CheckCircle2 size={14} />
              Looking good!
            </div>
          </div>
        ) : (
          <div className="capture-options single">
            <button className="capture-option primary" onClick={startCamera}>
              <div className="selfie-icon-wrapper">
                <User size={24} />
              </div>
              <span>Take Selfie</span>
              <small>Use front camera</small>
            </button>
          </div>
        )}

        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          capture="user"
          onChange={handleFileSelect}
          style={{ display: 'none' }}
        />
        <canvas ref={canvasRef} style={{ display: 'none' }} />
      </div>

      {/* Tips */}
      <div className="capture-tips">
        <div className="tip"><Sun size={14} /> Good lighting</div>
        <div className="tip"><Eye size={14} /> Look at camera</div>
        <div className="tip"><User size={14} /> No sunglasses</div>
      </div>

      {error && (
        <div className="error-message">
          <AlertCircle size={16} />
          {error}
        </div>
      )}

      {embed && error && !uploading && (
        <button
          type="button"
          className="sdk-btn secondary full-width embed-retry"
          onClick={() => {
            if (selfiePreview) URL.revokeObjectURL(selfiePreview);
            setSelfieFile(null);
            setSelfiePreview(null);
            setError(null);
          }}
        >
          <RefreshCw size={16} />
          <span>Try again</span>
        </button>
      )}

      {selfieFile && !cameraMode && (
        <button
          className="sdk-btn primary full-width"
          disabled={uploading}
          onClick={handleUpload}
        >
          {uploading ? (
            <>
              <Loader2 size={18} className="spin" />
              Uploading...
            </>
          ) : (
            <>
              <span>Continue</span>
              <ChevronRight size={18} />
            </>
          )}
        </button>
      )}

      {!cameraMode && !selfieFile && onBack && (
        <button className="back-button" onClick={onBack}>
          ← Back to Document
        </button>
      )}
    </div>
  );
};

// ============================================
// STEP 5: REVIEW & SUBMIT
// ============================================
const ReviewStep = ({ onComplete, onBack, status }) => {
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [animateIn, setAnimateIn] = useState(false);

  useEffect(() => {
    setTimeout(() => setAnimateIn(true), 100);
  }, []);

  const handleSubmit = async () => {
    setSubmitting(true);
    setError(null);

    try {
      await onComplete();
    } catch (err) {
      setError(err.message || 'Submission failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const completedSteps = [
    { label: 'Consent provided', done: true },
    { label: 'Personal information provided', done: status?.steps_completed?.includes('personal_info') },
    { label: 'ID document uploaded', done: status?.steps_completed?.includes('document') },
    { label: 'Selfie captured', done: status?.steps_completed?.includes('selfie') },
  ];

  return (
    <div className={`sdk-step review-step ${animateIn ? 'animate-in' : ''}`}>
      <div className="step-header">
        <div className="step-icon-wrapper success">
          <div className="step-icon-bg" />
          <CheckCircle size={32} className="step-icon" />
        </div>
        <h2>Ready to Submit</h2>
        <p className="step-subtitle">
          Review your submission and confirm
        </p>
      </div>

      <div className="review-checklist">
        {completedSteps.map((step, idx) => (
          <div
            key={idx}
            className={`checklist-item ${step.done ? 'done' : ''}`}
            style={{ animationDelay: `${idx * 100}ms` }}
          >
            <div className="checklist-icon">
              {step.done ? <CheckCircle2 size={20} /> : <div className="pending-dot" />}
            </div>
            <span>{step.label}</span>
          </div>
        ))}
      </div>

      <div className="submit-info">
        <Zap size={16} />
        <span>Verification typically completes within minutes</span>
      </div>

      {error && (
        <div className="error-message">
          <AlertCircle size={16} />
          {error}
        </div>
      )}

      {error && onBack && (
        <button
          className="sdk-btn secondary full-width"
          onClick={onBack}
          style={{ marginBottom: '8px' }}
        >
          <ChevronLeft size={18} />
          Go Back and Fix
        </button>
      )}

      <button
        className="sdk-btn primary full-width large"
        disabled={submitting}
        onClick={handleSubmit}
      >
        {submitting ? (
          <>
            <Loader2 size={20} className="spin" />
            Submitting...
          </>
        ) : (
          <>
            <Shield size={20} />
            {error ? 'Retry Submission' : 'Submit for Verification'}
          </>
        )}
      </button>

      {!error && onBack && (
        <button className="back-button" onClick={onBack}>
          ← Back to Selfie
        </button>
      )}
    </div>
  );
};

// ============================================
// STEP 6: EDD QUESTIONNAIRE (Additional Information)
// ============================================
const QuestionnaireStep = ({ questions, onComplete, onBack, client, submitFn }) => {
  const [answers, setAnswers] = useState({});
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [animateIn, setAnimateIn] = useState(false);

  useEffect(() => {
    setTimeout(() => setAnimateIn(true), 100);
  }, []);

  const handleChange = (id, value) => {
    setAnswers(prev => ({ ...prev, [id]: value }));
    if (errors[id]) {
      setErrors(prev => {
        const copy = { ...prev };
        delete copy[id];
        return copy;
      });
    }
  };

  const validate = () => {
    const newErrors = {};
    for (const q of questions) {
      if (q.required && answers[q.id] === undefined && answers[q.id] !== false) {
        newErrors[q.id] = 'This field is required';
      }
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setSubmitting(true);
    try {
      const submit = submitFn || (() => client.submitQuestionnaire(answers));
      await submit(answers);
      onComplete({ submitted: true });
    } catch (err) {
      setErrors({ _form: err.message });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className={`sdk-step questionnaire-step ${animateIn ? 'animate-in' : ''}`}>
      {/* Header */}
      <div className="step-header">
        <div className="step-icon-wrapper">
          <div className="step-icon-bg" />
          <ClipboardList size={32} className="step-icon" />
        </div>
        <h2>Additional Information</h2>
        <p className="step-subtitle">
          A few more questions to complete your verification.
          <br />
          <span style={{ opacity: 0.7, fontSize: '13px' }}>
            This is standard for all applicants.
          </span>
        </p>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit} className="questionnaire-form">
        {questions.map(q => (
          <div key={q.id} className="form-field">
            <label htmlFor={q.id}>
              {q.label}
              {q.required && <span className="required-star">*</span>}
            </label>

            {q.type === 'select' && (
              <select
                id={q.id}
                value={answers[q.id] || ''}
                onChange={(e) => handleChange(q.id, e.target.value)}
                className={errors[q.id] ? 'has-error' : ''}
              >
                <option value="">Select an option...</option>
                {q.options.map(opt => {
                  const val = typeof opt === 'string' ? opt : (opt.value || opt.label);
                  const label = typeof opt === 'string' ? opt : opt.label;
                  return <option key={val} value={val}>{label}</option>;
                })}
              </select>
            )}

            {q.type === 'multi_select' && (
              <div className="checkbox-group">
                {q.options.map(opt => {
                  const val = typeof opt === 'string' ? opt : (opt.value || opt.label);
                  const label = typeof opt === 'string' ? opt : opt.label;
                  const selected = Array.isArray(answers[q.id]) ? answers[q.id] : [];
                  return (
                    <label key={val} className="checkbox-option">
                      <input
                        type="checkbox"
                        checked={selected.includes(val)}
                        onChange={(e) => {
                          const updated = e.target.checked
                            ? [...selected, val]
                            : selected.filter(v => v !== val);
                          handleChange(q.id, updated);
                        }}
                      />
                      <span>{label}</span>
                    </label>
                  );
                })}
              </div>
            )}

            {q.type === 'boolean' && (
              <div className="boolean-group">
                <label className="radio-option">
                  <input
                    type="radio"
                    name={q.id}
                    checked={answers[q.id] === true}
                    onChange={() => handleChange(q.id, true)}
                  />
                  <span>Yes</span>
                </label>
                <label className="radio-option">
                  <input
                    type="radio"
                    name={q.id}
                    checked={answers[q.id] === false}
                    onChange={() => handleChange(q.id, false)}
                  />
                  <span>No</span>
                </label>
              </div>
            )}

            {q.type === 'text' && (
              <input
                type="text"
                id={q.id}
                value={answers[q.id] || ''}
                onChange={(e) => handleChange(q.id, e.target.value)}
                placeholder={q.placeholder || ''}
                className={errors[q.id] ? 'has-error' : ''}
              />
            )}

            {q.type === 'textarea' && (
              <textarea
                id={q.id}
                value={answers[q.id] || ''}
                onChange={(e) => handleChange(q.id, e.target.value)}
                placeholder={q.placeholder || ''}
                rows={3}
              />
            )}

            {q.type === 'date' && (
              <input
                type="date"
                id={q.id}
                value={answers[q.id] || ''}
                onChange={(e) => handleChange(q.id, e.target.value)}
                className={errors[q.id] ? 'has-error' : ''}
              />
            )}

            {q.helpText && <p className="help-text">{q.helpText}</p>}
            {errors[q.id] && (
              <p className="error-text">
                <AlertCircle size={14} /> {errors[q.id]}
              </p>
            )}
          </div>
        ))}

        {errors._form && (
          <div className="form-error">
            <AlertCircle size={16} /> {errors._form}
          </div>
        )}

        <div className="button-group">
          <button type="button" className="sdk-btn secondary" onClick={onBack}>
            <ChevronLeft size={18} /> Back
          </button>
          <button type="submit" className="sdk-btn primary" disabled={submitting}>
            {submitting ? (
              <>
                <Loader2 size={18} className="spin" /> Submitting...
              </>
            ) : (
              <>
                Continue <ChevronRight size={18} />
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

// ============================================
// COMPLETE SCREEN
// ============================================
const CompleteStep = ({ completion = {}, outcome, redirectUrl: legacyRedirectUrl, suppressRedirect = false }) => {
  const [animateIn, setAnimateIn] = useState(false);

  // Get settings from completion config (customer-customizable)
  const {
    title: defaultTitle = "Verification Submitted!",
    message: defaultMessage = "Thank you! Your verification is being processed. You'll be notified of the result shortly.",
    redirect_url: configRedirectUrl,
    redirect_delay_seconds: redirectDelay = 4,
    show_redirect_message: showRedirectMessage = true,
    approved_title: approvedTitle,
    approved_message: approvedMessage,
    review_title: reviewTitle,
    review_message: reviewMessage,
  } = completion;

  // Use legacy redirectUrl prop if no config redirect (backwards compatible).
  // Embed mode never redirects: the host owns navigation.
  const redirectUrl = suppressRedirect ? null : (configRedirectUrl || legacyRedirectUrl);

  // Determine which title/message to show based on outcome
  let title = defaultTitle;
  let message = defaultMessage;

  if (outcome === 'approved' && approvedTitle) {
    title = approvedTitle;
    message = approvedMessage || defaultMessage;
  } else if (outcome === 'review' && reviewTitle) {
    title = reviewTitle;
    message = reviewMessage || defaultMessage;
  }

  useEffect(() => {
    setTimeout(() => setAnimateIn(true), 100);

    if (redirectUrl) {
      const timer = setTimeout(() => {
        window.location.href = redirectUrl;
      }, redirectDelay * 1000);
      return () => clearTimeout(timer);
    }
  }, [redirectUrl, redirectDelay]);

  return (
    <div className={`sdk-step complete-step ${animateIn ? 'animate-in' : ''}`}>
      <div className="success-animation">
        <div className="success-circle">
          <CheckCircle2 size={48} />
        </div>
        <div className="success-rings">
          <div className="ring ring-1" />
          <div className="ring ring-2" />
          <div className="ring ring-3" />
        </div>
      </div>

      <h2>{title}</h2>
      <p className="step-subtitle">{message}</p>

      {redirectUrl && showRedirectMessage && (
        <div className="redirect-notice">
          <Loader2 size={14} className="spin" />
          Redirecting you back...
        </div>
      )}
    </div>
  );
};

// ============================================
// PROCESSING STEP (runs backend checks)
// ============================================
const ProcessingStep = ({ onComplete, client }) => {
  const [animateIn, setAnimateIn] = useState(false);
  const [currentCheck, setCurrentCheck] = useState(0);
  const [error, setError] = useState(null);

  const checks = [
    { icon: User, label: 'Comparing face to ID photo', key: 'face_match' },
    { icon: Shield, label: 'Running sanctions screening', key: 'sanctions' },
    { icon: ShieldCheck, label: 'Checking PEP databases', key: 'pep' },
  ];

  useEffect(() => {
    setTimeout(() => setAnimateIn(true), 100);
  }, []);

  // Simulate backend checks completing (in reality, poll /status endpoint)
  useEffect(() => {
    const runChecks = async () => {
      try {
        // Each check takes ~1.5s to simulate
        for (let i = 0; i < checks.length; i++) {
          await new Promise(resolve => setTimeout(resolve, 1500));
          setCurrentCheck(i + 1);
        }

        // All checks complete, auto-advance
        await new Promise(resolve => setTimeout(resolve, 500));
        onComplete({ processing_complete: true });
      } catch (err) {
        setError(err.message || 'Verification check failed');
      }
    };

    runChecks();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onComplete]);

  return (
    <div className={`sdk-step processing-step ${animateIn ? 'animate-in' : ''}`}>
      <div className="step-header">
        <div className="processing-spinner">
          <Loader2 size={32} className="spin" />
        </div>
        <h2>Verifying Your Identity</h2>
        <p className="step-subtitle">
          Please wait while we complete your verification checks.
          This usually takes less than a minute.
        </p>
      </div>

      <div className="checks-list">
        {checks.map((check, idx) => (
          <div
            key={check.key}
            className={`check-item ${idx < currentCheck ? 'completed' : ''} ${idx === currentCheck ? 'active' : ''}`}
          >
            <div className="check-icon">
              {idx < currentCheck ? (
                <CheckCircle2 size={20} />
              ) : idx === currentCheck ? (
                <Loader2 size={20} className="spin" />
              ) : (
                <check.icon size={20} />
              )}
            </div>
            <span>{check.label}</span>
          </div>
        ))}
      </div>

      {error && (
        <div className="error-message">
          <AlertCircle size={16} />
          {error}
        </div>
      )}

      <div className="processing-footer">
        <Lock size={14} />
        <span>Your data is encrypted and secure</span>
      </div>
    </div>
  );
};

// ============================================
// STEP CONFIGURATION
// ============================================

// Step labels for progress indicator
const STEP_LABELS = {
  consent: 'Consent',
  personal_info: 'Info',
  cdd_questionnaire: 'CDD',
  edd_questionnaire: 'Verify',
  document: 'Document',
  selfie: 'Selfie',
  liveness: 'Liveness',
  passive_liveness: 'Quick Check',
  active_liveness: 'Liveness',
  wallet_address: 'Wallet',
  address: 'Address',
  processing: 'Verify',
  review: 'Submit',
};

// Step icons for progress indicator
const STEP_ICONS = {
  consent: Shield,
  personal_info: UserCircle,
  cdd_questionnaire: ClipboardList,
  edd_questionnaire: ClipboardList,
  document: FileText,
  selfie: Camera,
  liveness: Scan,
  passive_liveness: Shield,
  active_liveness: Scan,
  wallet_address: Wallet,
  address: MapPin,
  processing: Loader2,
  review: CheckCircle,
};

// ============================================
// MAIN SDK COMPONENT
// ============================================
export default function VerificationSDK({
  accessToken,
  baseUrl = '/api/v1/sdk',
  onComplete,
  onError,
  theme = 'light',
  // Embed-only props. All default to "off" so /sdk/verify behaves as before.
  embed = null, // { onEvent(type, payload), reportError({code, message, retryable}) }
  whiteLabel: whiteLabelProp,
  prefill: prefillProp = null,
  brandingOverride = null,
}) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [config, setConfig] = useState(null);
  const [status, setStatus] = useState(null);
  const [currentStep, setCurrentStep] = useState(0);
  const [completed, setCompleted] = useState(false);
  const [submitResult, setSubmitResult] = useState(null);
  // Flow selector state (for unified KYC/KYB SDK)
  const [showFlowSelector, setShowFlowSelector] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [selectedEntityType, setSelectedEntityType] = useState(null);
  // Bumped by the embed "Try again" button to re-run init without a page
  // reload (a reload would drop the in-memory token).
  const [initAttempt, setInitAttempt] = useState(0);

  const client = useMemo(
    () => createSDKClient(accessToken, baseUrl),
    [accessToken, baseUrl]
  );

  useEffect(() => {
    const init = async () => {
      try {
        setLoading(true);
        const [configData, statusData] = await Promise.all([
          client.getConfig(),
          client.getStatus(),
        ]);

        setConfig(configData);
        setStatus(statusData);

        // Check if verification is already submitted
        if (statusData.status === 'submitted' || statusData.status === 'completed') {
          setCompleted(true);
          return;
        }

        // Check if flow selector should be shown
        // Show if: allowed_flows includes both "kyc" and "kyb" AND entity_type is not yet set
        const allowedFlows = configData.allowed_flows || [];
        const entityType = configData.entity_type;
        const needsFlowSelection =
          allowedFlows.includes('kyc') &&
          allowedFlows.includes('kyb') &&
          !entityType;

        if (needsFlowSelection) {
          setShowFlowSelector(true);
          setLoading(false);
          return;
        }

        // If entity_type is already set, store it
        if (entityType) {
          setSelectedEntityType(entityType);
        }

        // Use dynamic steps from config to find next uncompleted step
        const stepsCompleted = statusData.steps_completed || [];
        const allSteps = configData.steps?.map(s => s.name) || ['consent', 'personal_info', 'document', 'selfie', 'review'];
        const nextStep = allSteps.findIndex(s => !stepsCompleted.includes(s));

        // If all steps completed but not yet submitted, stay at last step
        if (nextStep === -1) {
          setCurrentStep(allSteps.length - 1);
        } else {
          setCurrentStep(nextStep);
        }

      } catch (err) {
        setError(err.message);
        onError?.(err);
      } finally {
        setLoading(false);
      }
    };

    if (accessToken) {
      init();
    }
  }, [accessToken, client, onError, initAttempt]);

  // Handle flow selection (when user chooses Individual or Business)
  const handleFlowSelect = async (entityType) => {
    try {
      const result = await client.selectFlow(entityType);
      setSelectedEntityType(entityType);
      setShowFlowSelector(false);

      // Update config with new steps if returned
      if (result.config) {
        setConfig(prev => ({
          ...prev,
          ...result.config,
          entity_type: entityType,
          flow_type: result.flow_type,
        }));
      }

      // Reset to first step
      setCurrentStep(0);
    } catch (err) {
      setError(err.message);
      onError?.(err);
    }
  };

  const handleSubmit = async () => {
    try {
      const result = await client.submit();
      setSubmitResult(result);
      setCompleted(true);
      onComplete?.(result);
      return result;
    } catch (err) {
      throw err;
    }
  };

  const handleStepComplete = async (stepName, data) => {
    // Track if we should advance even on backend failure (graceful degradation)
    let shouldAdvance = true;

    try {
      // Upload selfie blob from liveness steps (if captured)
      if (stepName === 'liveness' && data?.selfieBlob) {
        try {
          const selfieFile = new File([data.selfieBlob], 'liveness-selfie.jpg', { type: 'image/jpeg' });
          await client.uploadDocument(selfieFile, 'selfie', 'front');
        } catch (uploadErr) {
          console.warn('Failed to upload liveness selfie:', uploadErr);
        }
        // Remove blob from data before sending to completeStep (not serializable)
        const { selfieBlob, ...stepData } = data;
        await client.completeStep(stepName, stepData);
      } else {
        await client.completeStep(stepName, data);
      }

      try {
        const newStatus = await client.getStatus();
        setStatus(newStatus);
      } catch (statusErr) {
        console.warn('Failed to get status:', statusErr);
      }
    } catch (err) {
      // On 401/auth errors, still advance (user already did the work locally)
      // This prevents getting stuck when token expires mid-session
      if (err.message?.includes('401') || err.message?.includes('Unauthorized') || err.message?.includes('expired')) {
        console.warn(`Step ${stepName} backend sync failed (${err.message}), continuing anyway`);
      } else {
        // For other errors, show error but still try to advance
        console.warn(`Step ${stepName} error:`, err.message);
        // Embed mode: surface blocking errors inside the step so the user can
        // retry there. The step reports the error to the host.
        if (embed && (stepName === 'document' || stepName === 'personal_info')) {
          throw err;
        }
        setError(err.message);
        onError?.(err);
        // Don't advance on non-auth errors for critical steps
        if (stepName === 'document' || stepName === 'personal_info') {
          shouldAdvance = false;
        }
      }
    }

    // Advance to next step (even if backend sync failed)
    if (shouldAdvance) {
      const nextStepIndex = currentStep + 1;
      if (nextStepIndex >= steps.length) {
        // All steps completed - auto-submit
        try {
          await handleSubmit();
        } catch (submitErr) {
          console.warn('Submit failed:', submitErr.message);
          // Still mark as completed if submit fails
          setCompleted(true);
        }
      } else {
        setCurrentStep(nextStepIndex);
      }
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep((prev) => prev - 1);
    }
  };

  // Build steps dynamically from config (may include edd_questionnaire for high-risk countries)
  // STEP_LABELS and STEP_ICONS are module-level constants, so they're stable
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const steps = useMemo(() => {
    if (!config?.steps) {
      // Fallback to default 5 steps if config not loaded
      return [
        { key: 'consent', label: 'Consent', icon: Shield },
        { key: 'personal_info', label: 'Info', icon: UserCircle },
        { key: 'document', label: 'Document', icon: FileText },
        { key: 'selfie', label: 'Selfie', icon: Camera },
        { key: 'review', label: 'Submit', icon: CheckCircle },
      ];
    }

    return config.steps.map(step => ({
      key: step.name,
      label: STEP_LABELS[step.name] || step.title,
      icon: STEP_ICONS[step.name] || ClipboardList,
      config: step,
    }));
  }, [config]);

  // Get the current step key for conditional rendering
  const currentStepKey = steps[currentStep]?.key;
  const currentStepConfig = steps[currentStep]?.config;

  // Branding: embed sessions may carry their own branding, which wins over
  // the /config branding. Without an override this is config.branding as before.
  const branding = useMemo(() => {
    if (!brandingOverride) return config?.branding;
    return { ...(config?.branding || {}), ...brandingOverride };
  }, [config?.branding, brandingOverride]);

  // White label is opt-in: an explicit prop (embed session) or a flag the
  // backend returns on /config. Absent means the current behavior.
  const whiteLabel = typeof whiteLabelProp === 'boolean'
    ? whiteLabelProp
    : config?.white_label === true || config?.branding?.white_label === true;
  // Builder field config and prefill only apply to opted-in flows.
  const fieldConfigEnabled = !!embed || whiteLabel;
  const prefill = fieldConfigEnabled ? (prefillProp || config?.prefill || null) : null;
  const flowBranding = whiteLabel ? { ...(branding || {}), white_label: true } : branding;

  // Compute branding CSS variables from tenant config
  const brandingStyle = useMemo(() => {
    if (!branding) return {};
    const style = {};
    if (branding.primary_color) {
      style['--sdk-primary'] = branding.primary_color;
      style['--sdk-primary-rgb'] = hexToRgb(branding.primary_color);
    }
    if (branding.accent_color) {
      style['--sdk-accent'] = branding.accent_color;
    }
    if (fieldConfigEnabled) {
      // Opted-in flows: a solid brand color unless an accent is set, and the
      // brand hover color when provided.
      if (branding.primary_color && !branding.accent_color) {
        style['--sdk-accent'] = branding.primary_color;
      }
      const hover = branding.hover_color || branding.primary_hover_color;
      if (hover) style['--sdk-primary-dark'] = hover;
    }
    return style;
  }, [branding, fieldConfigEnabled]);

  // Embed events: step changes and completion. No-op without `embed`.
  const applicantId = config?.applicant_id || status?.applicant_id || null;
  useEffect(() => {
    if (!embed || loading || completed || showFlowSelector || !currentStepKey) return;
    embed.onEvent('step', { step: currentStepKey });
  }, [embed, loading, completed, showFlowSelector, currentStepKey]);

  const completeSentRef = useRef(false);
  useEffect(() => {
    if (!embed || !completed || completeSentRef.current) return;
    completeSentRef.current = true;
    embed.onEvent('complete', {
      applicantId,
      status: toCompleteStatus(submitResult?.outcome),
    });
  }, [embed, completed, applicantId, submitResult]);

  if (loading) {
    return (
      <div className={`verification-sdk ${theme}`} style={brandingStyle}>
        <div className="sdk-loading">
          <div className="loading-spinner">
            <Loader2 size={32} className="spin" />
          </div>
          <span>Preparing verification...</span>
        </div>
        <SDKStyles />
      </div>
    );
  }

  if (error && !config) {
    return (
      <div className={`verification-sdk ${theme}`} style={brandingStyle}>
        <div className="sdk-error">
          <AlertCircle size={48} />
          <h2>Unable to Load</h2>
          <p>{error}</p>
          <button
            className="sdk-btn primary"
            onClick={() => {
              if (embed) {
                // Re-run init in place; a reload would drop the in-memory token.
                setError(null);
                setInitAttempt((n) => n + 1);
              } else {
                window.location.reload();
              }
            }}
          >
            Try Again
          </button>
        </div>
        <SDKStyles />
      </div>
    );
  }

  if (completed) {
    return (
      <div className={`verification-sdk ${theme}`} style={brandingStyle}>
        <CompleteStep
          completion={config?.completion}
          outcome={submitResult?.outcome}
          redirectUrl={status?.redirect_url}
          suppressRedirect={!!embed}
        />
        {!whiteLabel && <PoweredByBadge />}
        <SDKStyles />
      </div>
    );
  }

  // Show flow selector when both KYC and KYB are enabled and user hasn't selected yet
  if (showFlowSelector) {
    return (
      <div className={`verification-sdk ${theme}`} style={brandingStyle}>
        <FlowSelectorStep
          onSelect={handleFlowSelect}
          config={config?.flow_selector || {}}
          branding={branding}
          whiteLabel={whiteLabel}
        />
        {!whiteLabel && <PoweredByBadge />}
        <SDKStyles />
      </div>
    );
  }

  return (
    <div className={`verification-sdk ${theme}`} style={brandingStyle}>
      {/* Progress bar */}
      <div className="sdk-progress-bar">
        {steps.map((step, idx) => (
          <div
            key={step.key}
            className={`progress-segment ${
              idx < currentStep ? 'completed' : ''
            } ${idx === currentStep ? 'active' : ''}`}
          >
            <div className="segment-track">
              <div className="segment-fill" />
            </div>
            <div className="segment-label">
              <step.icon size={14} />
              <span>{step.label}</span>
            </div>
          </div>
        ))}
      </div>

      {/* Current step content - rendered by step key for dynamic steps */}
      <div className="sdk-content">
        {currentStepKey === 'consent' && (
          <ConsentStep
            onComplete={(data) => handleStepComplete('consent', data)}
            branding={branding}
            whiteLabel={whiteLabel}
            stepConfig={currentStepConfig}
          />
        )}
        {currentStepKey === 'personal_info' && (
          <PersonalInfoStep
            onComplete={(data) => handleStepComplete('personal_info', data)}
            onBack={handleBack}
            client={client}
            stepConfig={currentStepConfig}
            fieldConfigEnabled={fieldConfigEnabled}
            prefill={prefill}
          />
        )}
        {currentStepKey === 'cdd_questionnaire' && (
          <QuestionnaireStep
            questions={currentStepConfig?.questions || []}
            onComplete={(data) => handleStepComplete('cdd_questionnaire', data)}
            onBack={handleBack}
            client={client}
            submitFn={async (answers) => {
              // Calculate risk scores from question options
              const riskScores = {};
              for (const q of (currentStepConfig?.questions || [])) {
                if (q.options && answers[q.id]) {
                  if (Array.isArray(answers[q.id])) {
                    // multi_select: sum risk scores of all selected options
                    const totalRisk = answers[q.id].reduce((sum, val) => {
                      const opt = q.options.find(o => o.value === val || o.label === val);
                      return sum + (opt?.riskScore || 0);
                    }, 0);
                    if (totalRisk) riskScores[q.id] = totalRisk;
                  } else {
                    const opt = q.options.find(o => o.value === answers[q.id] || o.label === answers[q.id]);
                    if (opt?.riskScore) riskScores[q.id] = opt.riskScore;
                  }
                }
              }
              await client.submitCddQuestionnaire(
                answers,
                currentStepConfig?.framework || 'cdd_fincen',
                riskScores
              );
            }}
          />
        )}
        {currentStepKey === 'edd_questionnaire' && (
          <QuestionnaireStep
            questions={currentStepConfig?.questions || []}
            onComplete={(data) => handleStepComplete('edd_questionnaire', data)}
            onBack={handleBack}
            client={client}
          />
        )}
        {currentStepKey === 'document' && (
          <DocumentStep
            onComplete={(data) => handleStepComplete('document', data)}
            onBack={handleBack}
            client={client}
          />
        )}
        {currentStepKey === 'selfie' && (
          <SelfieStep
            onComplete={(data) => handleStepComplete('selfie', data)}
            onBack={handleBack}
            client={client}
          />
        )}
        {currentStepKey === 'liveness' && (
          currentStepConfig?.liveness_provider === 'aws' ? (
            <LivenessStep
              onComplete={(data) => handleStepComplete('liveness', data)}
              onBack={handleBack}
              client={client}
            />
          ) : currentStepConfig?.liveness_provider === 'passive' ? (
            <PassiveLivenessStep
              onComplete={(data) => handleStepComplete('liveness', data)}
              onBack={handleBack}
              client={client}
            />
          ) : (
            <MediaPipeLivenessStep
              onComplete={(data) => handleStepComplete('liveness', data)}
              onBack={handleBack}
              client={client}
            />
          )
        )}

        {/* Passive Liveness - quick anti-spoof check (no user action needed) */}
        {currentStepKey === 'passive_liveness' && (
          <PassiveLivenessStep
            onComplete={(data) => handleStepComplete('passive_liveness', data)}
            onBack={handleBack}
            client={client}
          />
        )}

        {/* Active Liveness - challenge-based (head turns or AWS colored lights) */}
        {currentStepKey === 'active_liveness' && (
          currentStepConfig?.liveness_provider === 'aws' ? (
            <LivenessStep
              onComplete={(data) => handleStepComplete('active_liveness', data)}
              onBack={handleBack}
              client={client}
            />
          ) : (
            <MediaPipeLivenessStep
              onComplete={(data) => handleStepComplete('active_liveness', data)}
              onBack={handleBack}
              client={client}
            />
          )
        )}
        {currentStepKey === 'address' && (
          <AddressStep
            onComplete={(data) => handleStepComplete('address', data)}
            onBack={handleBack}
            client={client}
            stepConfig={currentStepConfig}
            fieldConfigEnabled={fieldConfigEnabled}
            prefill={prefill}
          />
        )}
        {currentStepKey === 'wallet_address' && (
          <WalletAddressStep
            onComplete={(data) => handleStepComplete('wallet_address', data)}
            onBack={handleBack}
            client={client}
            stepConfig={currentStepConfig}
          />
        )}
        {currentStepKey === 'processing' && (
          <ProcessingStep
            onComplete={(data) => handleStepComplete('processing', data)}
            client={client}
          />
        )}
        {currentStepKey === 'review' && (
          <ReviewStep
            onComplete={handleSubmit}
            onBack={handleBack}
            status={status}
          />
        )}

        {/* KYB Steps - Business verification flow */}
        {currentStepKey === 'company_info' && (
          <CompanyInfoStep
            config={currentStepConfig}
            branding={flowBranding}
            sessionData={status}
            onComplete={(data) => handleStepComplete('company_info', data)}
            onBack={handleBack}
            onError={(err) => setError(err.message || 'An error occurred')}
            apiClient={client}
          />
        )}
        {currentStepKey === 'beneficial_owners' && (
          <UBOCollectionStep
            config={currentStepConfig}
            branding={flowBranding}
            sessionData={status}
            onComplete={(data) => handleStepComplete('beneficial_owners', data)}
            onBack={handleBack}
            onError={(err) => setError(err.message || 'An error occurred')}
            apiClient={client}
          />
        )}
        {currentStepKey === 'corporate_documents' && (
          <CorporateDocumentsStep
            config={currentStepConfig}
            branding={flowBranding}
            sessionData={status}
            onComplete={(data) => handleStepComplete('corporate_documents', data)}
            onBack={handleBack}
            onError={(err) => setError(err.message || 'An error occurred')}
            apiClient={client}
          />
        )}
        {currentStepKey === 'kyb_review' && (
          <KYBReviewStep
            config={currentStepConfig}
            branding={flowBranding}
            sessionData={status}
            onComplete={handleSubmit}
            onBack={handleBack}
            onError={(err) => setError(err.message || 'An error occurred')}
            onEditSection={(section) => {
              // Navigate back to the section that needs editing
              const sectionStepIndex = steps.findIndex(s => s.key === section);
              if (sectionStepIndex >= 0) {
                setCurrentStep(sectionStepIndex);
              }
            }}
            apiClient={client}
          />
        )}
      </div>

      {!whiteLabel && <PoweredByBadge />}
      <SDKStyles />
    </div>
  );
}

// ============================================
// POWERED BY BADGE
// ============================================
function PoweredByBadge() {
  return (
    <div className="powered-by-badge">
      <a href="https://getclearance.com" target="_blank" rel="noopener noreferrer">
        <Shield size={12} />
        <span>Powered by <strong>{BRAND.name}</strong></span>
      </a>
    </div>
  );
}

// ============================================
// STYLES
// ============================================
function SDKStyles() {
  return (
    <style>{`
      /* Base */
      .verification-sdk {
        --primary: var(--sdk-primary, #0D9488);
        --primary-dark: var(--sdk-primary-dark, #0F766E);
        --primary-rgb: var(--sdk-primary-rgb, 13, 148, 136);
        --accent: var(--sdk-accent, var(--accent-secondary));
        --success: #10b981;
        --error: #ef4444;
        --gray-50: #f9fafb;
        --gray-100: #f3f4f6;
        --gray-200: #e5e7eb;
        --gray-300: #d1d5db;
        --gray-400: #9ca3af;
        --gray-500: #6b7280;
        --gray-600: #4b5563;
        --gray-700: #374151;
        --gray-800: #1f2937;
        --gray-900: #111827;

        font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', sans-serif;
        max-width: 440px;
        margin: 0 auto;
        background: white;
        border-radius: 24px;
        box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.15);
        overflow: hidden;
      }

      .verification-sdk.dark {
        --gray-50: #1f2937;
        --gray-100: #374151;
        --gray-200: #4b5563;
        --gray-700: #e5e7eb;
        --gray-800: #f3f4f6;
        --gray-900: #ffffff;
        background: #111827;
      }

      /* Loading */
      .sdk-loading {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        padding: 80px 24px;
        gap: 16px;
        color: var(--gray-500);
      }

      .loading-spinner {
        width: 64px;
        height: 64px;
        background: linear-gradient(135deg, var(--primary), var(--accent));
        border-radius: 16px;
        display: flex;
        align-items: center;
        justify-content: center;
        color: white;
      }

      /* Error */
      .sdk-error {
        display: flex;
        flex-direction: column;
        align-items: center;
        padding: 60px 24px;
        text-align: center;
        gap: 16px;
        color: var(--error);
      }

      .sdk-error h2 { color: var(--gray-900); margin: 0; }
      .sdk-error p { color: var(--gray-500); margin: 0; }

      /* Progress Bar */
      .sdk-progress-bar {
        display: flex;
        padding: 16px 24px;
        gap: 8px;
        background: var(--gray-50);
        border-bottom: 1px solid var(--gray-200);
      }

      .progress-segment {
        flex: 1;
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 6px;
      }

      .segment-track {
        height: 4px;
        background: var(--gray-200);
        border-radius: 2px;
        overflow: hidden;
      }

      .segment-fill {
        height: 100%;
        width: 0;
        background: var(--primary);
        transition: width 0.5s ease;
      }

      .progress-segment.completed .segment-fill,
      .progress-segment.active .segment-fill {
        width: 100%;
      }

      .progress-segment.completed .segment-fill {
        background: var(--success);
      }

      .segment-label {
        display: flex;
        align-items: center;
        justify-content: center;
        color: var(--gray-400);
      }

      /* Hide text labels - show only icons */
      .segment-label span {
        display: none;
      }

      .segment-label svg {
        width: 16px;
        height: 16px;
      }

      .progress-segment.active .segment-label {
        color: var(--primary);
      }

      .progress-segment.completed .segment-label {
        color: var(--success);
      }

      /* SDK Content */
      .sdk-content {
        padding: 32px 24px;
      }

      /* Neutral consent (white-label tenants only) */
      .neutral-consent-why {
        margin: 12px 0 0;
        font-size: 13px;
        line-height: 1.5;
        color: var(--gray-600);
      }
      .neutral-consent-link {
        display: inline-block;
        margin-top: 8px;
        font-size: 13px;
        font-weight: 600;
        color: var(--primary);
      }

      /* Powered By Badge */
      .powered-by-badge {
        text-align: center;
        padding: 12px 0 16px;
      }
      .powered-by-badge a {
        display: inline-flex;
        align-items: center;
        gap: 5px;
        font-size: 11px;
        color: var(--gray-400);
        text-decoration: none;
        transition: color 0.2s;
      }
      .powered-by-badge a:hover {
        color: var(--gray-600);
      }
      .powered-by-badge strong {
        font-weight: 600;
      }
      .powered-by-badge svg {
        opacity: 0.7;
      }

      /* Step Base */
      .sdk-step {
        opacity: 0;
        transform: translateY(20px);
        transition: all 0.4s ease;
      }

      .sdk-step.animate-in {
        opacity: 1;
        transform: translateY(0);
      }

      /* Step Header */
      .step-header {
        text-align: center;
        margin-bottom: 32px;
      }

      .step-header.compact {
        margin-bottom: 24px;
      }

      .step-icon-wrapper {
        position: relative;
        width: 72px;
        height: 72px;
        margin: 0 auto 20px;
      }

      .step-icon-bg {
        position: absolute;
        inset: 0;
        background: linear-gradient(135deg, var(--primary), var(--accent));
        border-radius: 20px;
        transform: rotate(-6deg);
      }

      .step-icon-wrapper.success .step-icon-bg {
        background: linear-gradient(135deg, var(--success), #34d399);
      }

      .step-icon {
        position: relative;
        z-index: 1;
        color: white;
        width: 100%;
        height: 100%;
        padding: 20px;
      }

      .sdk-lockup-logo {
        display: block;
        height: 30px;
        width: auto;
        max-width: 220px;
        object-fit: contain;
        margin: 0 auto 20px;
      }
      .sdk-tenant-logo {
        position: relative;
        z-index: 1;
        width: 100%;
        height: 100%;
        padding: 12px;
        object-fit: contain;
        border-radius: 12px;
      }

      .step-header h2 {
        font-size: 24px;
        font-weight: 700;
        color: var(--gray-900);
        margin: 0 0 8px;
      }

      .step-subtitle {
        font-size: 15px;
        color: var(--gray-500);
        margin: 0;
        line-height: 1.5;
      }

      /* Features Grid */
      .features-grid {
        display: flex;
        flex-direction: column;
        gap: 12px;
        margin-bottom: 24px;
      }

      .feature-card {
        display: flex;
        align-items: center;
        gap: 12px;
        padding: 14px 16px;
        background: var(--gray-50);
        border-radius: 12px;
        opacity: 0;
        animation: slideIn 0.4s ease forwards;
      }

      @keyframes slideIn {
        to { opacity: 1; transform: translateX(0); }
        from { opacity: 0; transform: translateX(-10px); }
      }

      .feature-card svg {
        color: var(--primary);
        flex-shrink: 0;
      }

      .feature-title {
        font-size: 14px;
        font-weight: 600;
        color: var(--gray-800);
      }

      .feature-desc {
        font-size: 12px;
        color: var(--gray-500);
      }

      /* Consent Box */
      .consent-box {
        background: var(--gray-50);
        border-radius: 16px;
        padding: 20px;
        margin-bottom: 20px;
      }

      .consent-header {
        display: flex;
        align-items: center;
        gap: 8px;
        font-size: 14px;
        font-weight: 600;
        color: var(--gray-700);
        margin-bottom: 12px;
      }

      .consent-list {
        list-style: none;
        padding: 0;
        margin: 0;
        display: flex;
        flex-direction: column;
        gap: 8px;
      }

      .consent-list li {
        display: flex;
        align-items: center;
        gap: 8px;
        font-size: 14px;
        color: var(--gray-600);
      }

      .consent-list svg {
        color: var(--success);
        flex-shrink: 0;
      }

      /* Consent Checkbox */
      .consent-checkbox {
        display: flex;
        align-items: flex-start;
        gap: 12px;
        padding: 16px;
        background: linear-gradient(135deg, #eff6ff, #f5f3ff);
        border-radius: 12px;
        cursor: pointer;
        margin-bottom: 24px;
        border: 2px solid transparent;
        transition: border-color 0.2s;
      }

      .consent-checkbox:has(input:checked) {
        border-color: var(--primary);
      }

      .consent-checkbox input {
        display: none;
      }

      .checkbox-custom {
        width: 22px;
        height: 22px;
        border: 2px solid var(--gray-300);
        border-radius: 6px;
        display: flex;
        align-items: center;
        justify-content: center;
        flex-shrink: 0;
        transition: all 0.2s;
        margin-top: 1px;
      }

      .consent-checkbox:has(input:checked) .checkbox-custom {
        background: var(--primary);
        border-color: var(--primary);
        color: white;
      }

      .consent-checkbox span {
        font-size: 14px;
        color: var(--gray-700);
        line-height: 1.5;
      }

      .secure-notice {
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 6px;
        font-size: 12px;
        color: var(--gray-400);
        margin-top: 16px;
      }

      /* Document Type Grid */
      .document-type-grid {
        display: flex;
        flex-direction: column;
        gap: 12px;
      }

      .document-type-card {
        display: flex;
        align-items: center;
        gap: 16px;
        padding: 18px 20px;
        background: white;
        border: 2px solid var(--gray-200);
        border-radius: 16px;
        cursor: pointer;
        transition: all 0.2s;
        text-align: left;
        width: 100%;
        opacity: 0;
        animation: slideIn 0.4s ease forwards;
      }

      .document-type-card:hover {
        border-color: var(--primary);
        background: var(--gray-50);
      }

      .doc-type-icon {
        width: 48px;
        height: 48px;
        background: linear-gradient(135deg, var(--primary), var(--accent));
        border-radius: 12px;
        display: flex;
        align-items: center;
        justify-content: center;
        color: white;
      }

      .doc-type-info {
        flex: 1;
      }

      .doc-type-label {
        font-size: 16px;
        font-weight: 600;
        color: var(--gray-800);
      }

      .doc-type-desc {
        font-size: 13px;
        color: var(--gray-500);
      }

      .doc-type-arrow {
        color: var(--gray-400);
      }

      /* Capture Progress */
      .capture-progress {
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 0;
        margin-bottom: 24px;
      }

      .capture-dot {
        width: 28px;
        height: 28px;
        border-radius: 50%;
        background: var(--gray-200);
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 12px;
        font-weight: 600;
        color: var(--gray-500);
        transition: all 0.3s;
      }

      .capture-dot.active {
        background: var(--primary);
        color: white;
        transform: scale(1.1);
      }

      .capture-dot.done {
        background: var(--success);
        color: white;
      }

      .capture-line {
        width: 60px;
        height: 2px;
        background: var(--gray-200);
      }

      /* Capture Area */
      .capture-area {
        margin-bottom: 24px;
      }

      .capture-options {
        display: flex;
        flex-wrap: wrap;
        justify-content: center;
        gap: 12px;
      }

      .capture-options .capture-option {
        flex: 0 1 calc(50% - 6px);
        max-width: 200px;
      }

      .capture-options:has(.capture-option:only-child) .capture-option {
        flex: 0 1 auto;
        min-width: 160px;
      }

      .capture-option {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 8px;
        padding: 28px 16px;
        background: var(--gray-50);
        border: 2px solid var(--gray-200);
        border-radius: 16px;
        cursor: pointer;
        transition: all 0.2s;
        color: var(--gray-700);
      }

      .capture-option:hover {
        border-color: var(--primary);
        background: white;
      }

      .capture-option.primary {
        background: linear-gradient(135deg, var(--primary), var(--accent));
        border-color: transparent;
        color: white;
      }

      .capture-option.primary:hover {
        transform: translateY(-2px);
        box-shadow: 0 8px 20px rgba(var(--primary-rgb), 0.3);
      }

      .capture-option span {
        font-size: 14px;
        font-weight: 600;
      }

      .capture-option small {
        font-size: 12px;
        opacity: 0.7;
      }

      .selfie-icon-wrapper {
        width: 48px;
        height: 48px;
        border: 2px solid rgba(255,255,255,0.3);
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
      }

      /* Camera Container */
      .camera-container {
        position: relative;
        border-radius: 20px;
        overflow: hidden;
        background: #000;
        max-height: calc(100vh - 200px);
        max-height: calc(100dvh - 200px);
      }

      .camera-container video {
        width: 100%;
        height: 100%;
        min-height: 280px;
        max-height: calc(100vh - 200px);
        max-height: calc(100dvh - 200px);
        display: block;
        object-fit: cover;
      }

      .selfie-camera video {
        transform: scaleX(-1);
      }

      /* Document Frame */
      .document-frame {
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        width: 85%;
        aspect-ratio: 1.586;
        pointer-events: none;
      }

      .frame-corner {
        position: absolute;
        width: 24px;
        height: 24px;
        border: 3px solid rgba(255,255,255,0.8);
      }

      .frame-corner.tl { top: 0; left: 0; border-right: none; border-bottom: none; border-radius: 8px 0 0 0; }
      .frame-corner.tr { top: 0; right: 0; border-left: none; border-bottom: none; border-radius: 0 8px 0 0; }
      .frame-corner.bl { bottom: 0; left: 0; border-right: none; border-top: none; border-radius: 0 0 0 8px; }
      .frame-corner.br { bottom: 0; right: 0; border-left: none; border-top: none; border-radius: 0 0 8px 0; }

      /* Face Oval */
      .face-oval {
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -55%);
        width: 200px;
        height: 260px;
        pointer-events: none;
      }

      .oval-border {
        width: 100%;
        height: 100%;
        border: 3px solid rgba(255,255,255,0.7);
        border-radius: 50%;
        animation: pulse-border 2s ease-in-out infinite;
      }

      @keyframes pulse-border {
        0%, 100% { opacity: 0.7; transform: scale(1); }
        50% { opacity: 1; transform: scale(1.02); }
      }

      /* Countdown */
      .countdown-overlay {
        position: absolute;
        inset: 0;
        background: rgba(0,0,0,0.5);
        display: flex;
        align-items: center;
        justify-content: center;
      }

      .countdown-number {
        width: 80px;
        height: 80px;
        background: white;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 36px;
        font-weight: 700;
        color: var(--primary);
        animation: pop 0.3s ease;
      }

      @keyframes pop {
        0% { transform: scale(0.5); opacity: 0; }
        100% { transform: scale(1); opacity: 1; }
      }

      /* Camera Guidance */
      .camera-guidance {
        position: absolute;
        bottom: 80px;
        left: 50%;
        transform: translateX(-50%);
        background: rgba(0,0,0,0.7);
        color: white;
        padding: 10px 20px;
        border-radius: 20px;
        font-size: 14px;
        font-weight: 500;
        display: flex;
        align-items: center;
        gap: 8px;
        white-space: nowrap;
      }

      /* Camera Controls */
      .camera-controls {
        position: absolute;
        bottom: 16px;
        left: 0;
        right: 0;
        display: flex;
        justify-content: center;
        align-items: center;
        gap: 32px;
      }

      .camera-btn {
        background: none;
        border: none;
        cursor: pointer;
        transition: all 0.2s;
      }

      .camera-btn.cancel {
        width: 44px;
        height: 44px;
        background: rgba(255,255,255,0.2);
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        color: white;
      }

      .camera-btn.capture {
        width: 68px;
        height: 68px;
        background: white;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        box-shadow: 0 4px 12px rgba(0,0,0,0.3);
      }

      .camera-btn.capture:hover {
        transform: scale(1.05);
      }

      .camera-btn.capture:disabled {
        opacity: 0.5;
      }

      .capture-ring {
        width: 52px;
        height: 52px;
        border: 4px solid var(--primary);
        border-radius: 50%;
      }

      .camera-btn.placeholder {
        width: 44px;
      }

      /* Preview Container */
      .preview-container {
        position: relative;
        border-radius: 20px;
        overflow: hidden;
        background: var(--gray-100);
      }

      .preview-container img {
        width: 100%;
        display: block;
      }

      .preview-overlay {
        position: absolute;
        inset: 0;
        background: linear-gradient(transparent 60%, rgba(0,0,0,0.6));
        display: flex;
        align-items: flex-end;
        justify-content: center;
        padding: 20px;
      }

      .retake-btn {
        display: flex;
        align-items: center;
        gap: 6px;
        padding: 10px 20px;
        background: rgba(255,255,255,0.95);
        border: none;
        border-radius: 20px;
        font-size: 14px;
        font-weight: 500;
        cursor: pointer;
        transition: all 0.2s;
      }

      .retake-btn:hover {
        background: white;
        transform: scale(1.02);
      }

      .preview-badge {
        position: absolute;
        top: 16px;
        right: 16px;
        display: flex;
        align-items: center;
        gap: 6px;
        padding: 8px 14px;
        background: white;
        border-radius: 20px;
        font-size: 13px;
        font-weight: 500;
      }

      .preview-badge.success {
        color: var(--success);
      }

      /* Capture Tips */
      .capture-tips {
        display: flex;
        justify-content: center;
        gap: 16px;
        margin-bottom: 24px;
      }

      .tip {
        display: flex;
        align-items: center;
        gap: 6px;
        font-size: 12px;
        color: var(--gray-500);
        background: var(--gray-50);
        padding: 6px 12px;
        border-radius: 16px;
      }

      /* Processing Overlay */
      .processing-overlay {
        position: fixed;
        inset: 0;
        background: rgba(255,255,255,0.95);
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        gap: 16px;
        z-index: 100;
        color: var(--gray-700);
        font-size: 15px;
      }

      /* Review Checklist */
      .review-checklist {
        display: flex;
        flex-direction: column;
        gap: 12px;
        margin-bottom: 24px;
      }

      .checklist-item {
        display: flex;
        align-items: center;
        gap: 14px;
        padding: 16px 18px;
        background: var(--gray-50);
        border-radius: 12px;
        opacity: 0;
        animation: slideIn 0.4s ease forwards;
      }

      .checklist-item.done {
        background: linear-gradient(135deg, #ecfdf5, #f0fdf4);
      }

      .checklist-icon {
        width: 24px;
        height: 24px;
        display: flex;
        align-items: center;
        justify-content: center;
        color: var(--gray-400);
      }

      .checklist-item.done .checklist-icon {
        color: var(--success);
      }

      .pending-dot {
        width: 8px;
        height: 8px;
        background: var(--gray-300);
        border-radius: 50%;
      }

      .checklist-item span {
        font-size: 15px;
        color: var(--gray-700);
      }

      .submit-info {
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 8px;
        padding: 12px;
        background: #fffbeb;
        border-radius: 10px;
        font-size: 13px;
        color: #b45309;
        margin-bottom: 24px;
      }

      /* Success Animation */
      .success-animation {
        position: relative;
        width: 120px;
        height: 120px;
        margin: 0 auto 32px;
      }

      .success-circle {
        position: absolute;
        inset: 20px;
        background: linear-gradient(135deg, var(--success), #34d399);
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        color: white;
        animation: pop 0.5s ease;
      }

      .success-rings {
        position: absolute;
        inset: 0;
      }

      .ring {
        position: absolute;
        inset: 0;
        border: 2px solid var(--success);
        border-radius: 50%;
        opacity: 0;
        animation: ripple 2s ease-out infinite;
      }

      .ring-2 { animation-delay: 0.5s; }
      .ring-3 { animation-delay: 1s; }

      @keyframes ripple {
        0% { transform: scale(0.7); opacity: 0.6; }
        100% { transform: scale(1.3); opacity: 0; }
      }

      .complete-step h2 {
        text-align: center;
        font-size: 26px;
        color: var(--gray-900);
        margin: 0 0 12px;
      }

      .complete-step .step-subtitle {
        text-align: center;
        max-width: 300px;
        margin: 0 auto;
      }

      .redirect-notice {
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 8px;
        margin-top: 24px;
        font-size: 14px;
        color: var(--gray-500);
      }

      /* Processing Step */
      .processing-step {
        padding: 60px 24px;
        text-align: center;
      }

      .processing-step .step-header {
        margin-bottom: 32px;
      }

      .processing-step .processing-spinner {
        width: 80px;
        height: 80px;
        background: linear-gradient(135deg, var(--primary), var(--accent));
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        margin: 0 auto 24px;
        color: white;
      }

      .processing-step h2 {
        font-size: 24px;
        color: var(--gray-900);
        margin: 0 0 12px;
      }

      .processing-step .step-subtitle {
        color: var(--gray-500);
        max-width: 280px;
        margin: 0 auto;
      }

      .processing-step .checks-list {
        display: flex;
        flex-direction: column;
        gap: 16px;
        max-width: 280px;
        margin: 0 auto 32px;
        text-align: left;
      }

      .processing-step .check-item {
        display: flex;
        align-items: center;
        gap: 12px;
        padding: 12px 16px;
        background: var(--gray-50);
        border-radius: 12px;
        color: var(--gray-500);
        transition: all 0.3s ease;
      }

      .processing-step .check-item.active {
        background: rgba(var(--primary-rgb), 0.1);
        color: var(--primary);
      }

      .processing-step .check-item.completed {
        background: rgba(16, 185, 129, 0.1);
        color: var(--success);
      }

      .processing-step .check-icon {
        width: 32px;
        height: 32px;
        border-radius: 8px;
        display: flex;
        align-items: center;
        justify-content: center;
        background: white;
      }

      .processing-step .check-item.completed .check-icon {
        background: var(--success);
        color: white;
      }

      .processing-step .processing-footer {
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 8px;
        color: var(--gray-400);
        font-size: 12px;
      }

      /* Error Message */
      .error-message {
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 8px;
        padding: 14px;
        background: #fef2f2;
        border: 1px solid #fecaca;
        border-radius: 12px;
        color: var(--error);
        font-size: 14px;
        margin-bottom: 16px;
      }

      /* Buttons */
      .sdk-btn {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        gap: 8px;
        padding: 14px 28px;
        font-size: 15px;
        font-weight: 600;
        border-radius: 12px;
        border: none;
        cursor: pointer;
        transition: all 0.2s;
        font-family: inherit;
      }

      .sdk-btn.full-width {
        width: 100%;
      }

      .sdk-btn.large {
        padding: 18px 32px;
        font-size: 16px;
        border-radius: 14px;
      }

      .sdk-btn.primary {
        background: linear-gradient(135deg, var(--primary), var(--accent));
        color: white;
      }

      .sdk-btn.primary:hover:not(:disabled) {
        transform: translateY(-2px);
        box-shadow: 0 8px 20px rgba(var(--primary-rgb), 0.35);
      }

      .sdk-btn.primary:disabled {
        opacity: 0.5;
        cursor: not-allowed;
        transform: none;
        box-shadow: none;
      }

      /* Back Button */
      .back-button {
        display: block;
        width: 100%;
        padding: 12px;
        margin-top: 12px;
        background: transparent;
        border: none;
        color: var(--gray-500);
        font-size: 14px;
        font-weight: 500;
        cursor: pointer;
        transition: color 0.2s;
        text-align: center;
      }

      .back-button:hover {
        color: var(--gray-700);
      }

      /* Spin Animation */
      @keyframes spin {
        from { transform: rotate(0deg); }
        to { transform: rotate(360deg); }
      }

      .spin { animation: spin 1s linear infinite; }

      /* Personal Info Form */
      .personal-info-form {
        display: flex;
        flex-direction: column;
        gap: 20px;
        margin-bottom: 24px;
      }

      .form-row {
        display: flex;
        gap: 12px;
      }

      .form-row.two-col > .form-group {
        flex: 1;
      }

      .form-group {
        display: flex;
        flex-direction: column;
        gap: 6px;
      }

      .form-group label {
        display: flex;
        align-items: center;
        gap: 6px;
        font-size: 13px;
        font-weight: 500;
        color: var(--gray-600);
      }

      .form-group label svg {
        color: var(--gray-400);
      }

      .form-group input,
      .form-group select {
        padding: 12px 14px;
        border: 2px solid var(--gray-200);
        border-radius: 10px;
        font-size: 15px;
        font-family: inherit;
        transition: all 0.2s;
        background: white;
        color: var(--gray-800);
      }

      .form-group input:focus,
      .form-group select:focus {
        outline: none;
        border-color: var(--primary);
        box-shadow: 0 0 0 3px rgba(var(--primary-rgb), 0.1);
      }

      .form-group input.error,
      .form-group select.error {
        border-color: var(--error);
      }

      .form-group input::placeholder {
        color: var(--gray-400);
      }

      .field-error {
        font-size: 12px;
        color: var(--error);
      }

      /* Modern Date Picker (Stripe-style) */
      .date-picker-modern {
        width: 100%;
      }

      .date-picker-row {
        display: flex;
        gap: 8px;
      }

      .date-select {
        flex: 1;
        padding: 12px 10px;
        border: 2px solid var(--gray-200);
        border-radius: 10px;
        font-size: 14px;
        font-family: inherit;
        background: white;
        color: var(--gray-800);
        cursor: pointer;
        transition: all 0.2s;
        appearance: none;
        background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%236b7280' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E");
        background-repeat: no-repeat;
        background-position: right 10px center;
        padding-right: 28px;
      }

      .date-select:focus {
        outline: none;
        border-color: var(--primary);
        box-shadow: 0 0 0 3px rgba(var(--primary-rgb), 0.1);
      }

      .date-select.month-select {
        flex: 1.5;
      }

      .date-select.day-select {
        flex: 0.8;
      }

      .date-select.year-select {
        flex: 1;
      }

      .date-picker-modern.has-error .date-select {
        border-color: var(--error);
      }

      /* Address Autocomplete */
      .address-autocomplete {
        position: relative;
        width: 100%;
      }

      .address-autocomplete input {
        width: 100%;
        padding: 12px 14px;
        border: 2px solid var(--gray-200);
        border-radius: 10px;
        font-size: 15px;
        font-family: inherit;
        transition: all 0.2s;
        background: white;
        color: var(--gray-800);
      }

      .address-autocomplete input:focus {
        outline: none;
        border-color: var(--primary);
        box-shadow: 0 0 0 3px rgba(var(--primary-rgb), 0.1);
      }

      .autocomplete-loading {
        position: absolute;
        right: 12px;
        top: 50%;
        transform: translateY(-50%);
        color: var(--gray-400);
      }

      .autocomplete-dropdown {
        position: absolute;
        top: calc(100% + 4px);
        left: 0;
        right: 0;
        background: white;
        border: 1px solid var(--gray-200);
        border-radius: 12px;
        box-shadow: 0 10px 40px rgba(0, 0, 0, 0.12);
        z-index: 100;
        max-height: 200px;
        overflow-y: auto;
      }

      .autocomplete-item {
        display: flex;
        align-items: center;
        gap: 10px;
        padding: 12px 14px;
        cursor: pointer;
        transition: background 0.15s;
        font-size: 14px;
        color: var(--gray-700);
      }

      .autocomplete-item:hover {
        background: var(--gray-50);
      }

      .autocomplete-item:first-child {
        border-radius: 12px 12px 0 0;
      }

      .autocomplete-item:last-child {
        border-radius: 0 0 12px 12px;
      }

      .autocomplete-item svg {
        color: var(--gray-400);
        flex-shrink: 0;
      }

      .form-section {
        background: var(--gray-50);
        border-radius: 16px;
        padding: 16px;
      }

      .section-header {
        display: flex;
        align-items: center;
        gap: 8px;
        font-size: 14px;
        font-weight: 600;
        color: var(--gray-600);
        margin-bottom: 16px;
      }

      .section-header svg {
        color: var(--primary);
      }

      .form-section .form-group {
        margin-bottom: 12px;
      }

      .form-section .form-group:last-child {
        margin-bottom: 0;
      }

      .form-section .form-row {
        margin-bottom: 12px;
      }

      .form-section .form-row:last-child {
        margin-bottom: 0;
      }

      .form-section label {
        font-size: 12px;
      }

      .form-section input,
      .form-section select {
        padding: 10px 12px;
        font-size: 14px;
      }

      .form-section .address-autocomplete input {
        padding: 10px 12px;
        font-size: 14px;
      }

      /* Questionnaire Step (EDD) */
      .questionnaire-step .questionnaire-form {
        display: flex;
        flex-direction: column;
        gap: 20px;
      }

      .questionnaire-step .form-field {
        display: flex;
        flex-direction: column;
        gap: 6px;
      }

      .questionnaire-step .form-field label {
        font-size: 14px;
        font-weight: 500;
        color: var(--gray-700);
        display: flex;
        align-items: center;
        gap: 4px;
      }

      .questionnaire-step .required-star {
        color: var(--error);
      }

      .questionnaire-step select,
      .questionnaire-step textarea {
        padding: 12px 14px;
        border: 2px solid var(--gray-200);
        border-radius: 10px;
        font-size: 15px;
        font-family: inherit;
        transition: all 0.2s;
        background: white;
        color: var(--gray-800);
      }

      .questionnaire-step select:focus,
      .questionnaire-step textarea:focus {
        outline: none;
        border-color: var(--primary);
        box-shadow: 0 0 0 3px rgba(var(--primary-rgb), 0.1);
      }

      .questionnaire-step select.has-error,
      .questionnaire-step textarea.has-error {
        border-color: var(--error);
      }

      .questionnaire-step .checkbox-group {
        display: flex;
        flex-direction: column;
        gap: 10px;
        padding: 8px 0;
      }

      .questionnaire-step .checkbox-option {
        display: flex;
        align-items: center;
        gap: 10px;
        cursor: pointer;
        font-size: 15px;
        color: var(--gray-700);
        padding: 8px 12px;
        border: 1px solid var(--gray-200);
        border-radius: 8px;
        transition: border-color 0.15s;
      }

      .questionnaire-step .checkbox-option:hover {
        border-color: var(--primary);
      }

      .questionnaire-step .checkbox-option input {
        width: 18px;
        height: 18px;
        accent-color: var(--primary);
        cursor: pointer;
      }

      .questionnaire-step .boolean-group {
        display: flex;
        gap: 24px;
        padding: 8px 0;
      }

      .questionnaire-step .radio-option {
        display: flex;
        align-items: center;
        gap: 8px;
        cursor: pointer;
        font-size: 15px;
        color: var(--gray-700);
      }

      .questionnaire-step .radio-option input {
        width: 18px;
        height: 18px;
        accent-color: var(--primary);
        cursor: pointer;
      }

      .questionnaire-step .help-text {
        font-size: 12px;
        color: var(--gray-500);
        margin: 0;
      }

      .questionnaire-step .error-text {
        font-size: 12px;
        color: var(--error);
        display: flex;
        align-items: center;
        gap: 4px;
        margin: 0;
      }

      .questionnaire-step .form-error {
        background: #fef2f2;
        border: 1px solid #fecaca;
        border-radius: 10px;
        padding: 12px;
        color: var(--error);
        display: flex;
        align-items: center;
        gap: 8px;
        font-size: 14px;
      }

      .questionnaire-step .button-group {
        display: flex;
        justify-content: space-between;
        margin-top: 12px;
      }

      /* ==============================================
         MOBILE-FIRST 2026 iOS STANDARDS
         Based on Apple HIG 2026 + View Transitions API
         ============================================== */

      /* View Transitions for native-like step changes */
      @view-transition {
        navigation: auto;
      }

      .sdk-step {
        view-transition-name: sdk-step;
      }

      ::view-transition-old(sdk-step) {
        animation: slide-out-left 250ms ease-out;
      }

      ::view-transition-new(sdk-step) {
        animation: slide-in-right 250ms ease-out;
      }

      @keyframes slide-out-left {
        from { transform: translateX(0); opacity: 1; }
        to { transform: translateX(-30%); opacity: 0; }
      }

      @keyframes slide-in-right {
        from { transform: translateX(30%); opacity: 0; }
        to { transform: translateX(0); opacity: 1; }
      }

      /* Reduced motion accessibility */
      @media (prefers-reduced-motion: reduce) {
        *, ::view-transition-old(*), ::view-transition-new(*) {
          animation-duration: 0.01ms !important;
          transition-duration: 0.01ms !important;
        }
      }

      /* Mobile - iOS Standards 2026 */
      @media (max-width: 480px) {
        .verification-sdk {
          max-width: 100%;
          border-radius: 0;
          min-height: 100vh;
          min-height: 100dvh;
          /* iOS Safe Areas */
          padding-top: env(safe-area-inset-top);
          padding-left: env(safe-area-inset-left);
          padding-right: env(safe-area-inset-right);
        }

        /* Progress bar - clean, visible */
        .sdk-progress-bar {
          padding: 12px 16px;
          gap: 4px;
          padding-top: calc(12px + env(safe-area-inset-top));
        }

        .progress-segment {
          gap: 4px;
        }

        .segment-label svg {
          width: 14px;
          height: 14px;
        }

        .segment-track {
          height: 5px;
          border-radius: 3px;
        }

        /* Content area with proper padding */
        .sdk-content {
          padding: 12px 16px;
          padding-bottom: calc(12px + env(safe-area-inset-bottom));
        }

        /* Consent step specific - ensure it fits on screen */
        .consent-step {
          display: flex;
          flex-direction: column;
        }

        .consent-step .sdk-btn.primary.full-width {
          margin-top: auto;
          padding: 14px 20px;
        }

        /* ======================
           TYPOGRAPHY - Apple HIG 2026
           Body: 17px
           Secondary: 15px
           Minimum: 11px
           Titles: 22px
           ====================== */

        /* Step headers - compact for mobile */
        .step-icon-wrapper {
          width: 48px;
          height: 48px;
          margin-bottom: 8px;
        }

        .step-icon {
          padding: 12px;
        }

        .step-icon-bg {
          border-radius: 14px;
        }

        .step-header h2 {
          font-size: 20px;
          font-weight: 600;
          margin-bottom: 4px;
          letter-spacing: -0.02em;
        }

        .step-header .step-subtitle {
          font-size: 15px;
          line-height: 1.3;
          color: #6B7280;
        }

        .step-header {
          margin-bottom: 12px;
        }

        /* Feature cards - more compact for consent screen */
        .features-grid {
          gap: 6px;
          margin-bottom: 10px;
        }

        .feature-card {
          padding: 10px 12px;
          gap: 10px;
          border-radius: 10px;
          min-height: 40px; /* Touch target */
        }

        .feature-card svg {
          width: 18px;
          height: 18px;
          flex-shrink: 0;
        }

        .feature-title {
          font-size: 14px;
          font-weight: 500;
        }

        .feature-desc {
          font-size: 13px;
          color: #6B7280;
        }

        /* Consent box - compact */
        .consent-box {
          padding: 12px;
          margin-bottom: 10px;
          border-radius: 10px;
        }

        .consent-header {
          font-size: 14px;
          font-weight: 600;
          margin-bottom: 6px;
        }

        .consent-list {
          gap: 4px;
        }

        .consent-list li {
          font-size: 14px;
          line-height: 1.3;
        }

        /* Consent checkbox - 44px touch target */
        .consent-checkbox {
          padding: 12px 14px;
          margin-bottom: 10px;
          min-height: 44px;
          border-radius: 10px;
        }

        .consent-checkbox input[type="checkbox"] {
          width: 22px;
          height: 22px;
          margin-right: 10px;
        }

        .consent-checkbox span {
          font-size: 14px;
        }

        .secure-notice {
          font-size: 12px;
          margin-top: 8px;
        }

        /* ======================
           CAPTURE STEPS
           ====================== */

        .capture-area {
          display: flex;
          flex-direction: column;
          align-items: center;
          margin-bottom: 16px;
        }

        .camera-container {
          max-height: 40vh;
          max-height: 40dvh;
          border-radius: 16px;
        }

        .camera-container video {
          max-height: 40vh;
          max-height: 40dvh;
        }

        /* Selfie camera */
        .selfie-camera {
          max-height: 45vh;
          max-height: 45dvh;
          aspect-ratio: 3/4;
        }

        .selfie-camera video {
          max-height: 45vh;
          max-height: 45dvh;
        }

        /* Face oval - proportional to camera */
        .face-oval {
          width: 55%;
          max-width: 160px;
          height: auto;
          aspect-ratio: 3/4;
          transform: translate(-50%, -50%);
        }

        /* Selfie preview */
        .selfie-preview {
          max-height: 40vh;
          max-height: 40dvh;
        }

        .selfie-preview img {
          max-height: 40vh;
          max-height: 40dvh;
          width: auto;
          object-fit: contain;
        }

        /* Capture options - proper touch targets */
        .capture-options {
          gap: 12px;
        }

        .capture-options .capture-option {
          flex: 0 1 calc(50% - 6px);
          max-width: none;
          padding: 20px 16px;
          border-radius: 16px;
          min-height: 80px; /* Comfortable tap */
        }

        .capture-options:has(.capture-option:only-child) .capture-option {
          flex: 0 1 100%;
          max-width: 280px;
        }

        .capture-option span {
          font-size: 17px;
          font-weight: 500;
        }

        .capture-option small {
          font-size: 15px;
          color: rgba(255,255,255,0.8);
        }

        /* Capture tips */
        .capture-tips {
          flex-wrap: wrap;
          gap: 8px;
          margin-bottom: 16px;
        }

        .capture-tips .tip {
          font-size: 15px;
          padding: 8px 12px;
          border-radius: 20px;
        }

        /* Preview container */
        .preview-container {
          margin-bottom: 16px;
          border-radius: 16px;
          width: 100%;
          max-width: 300px;
        }

        .preview-container img {
          max-height: 35vh;
          max-height: 35dvh;
          object-fit: contain;
          border-radius: 12px;
        }

        .preview-badge {
          font-size: 15px;
          padding: 8px 16px;
          border-radius: 20px;
        }

        /* Retake button - 44px touch target */
        .retake-btn {
          font-size: 17px;
          padding: 12px 20px;
          min-height: 44px;
          border-radius: 22px;
        }

        /* ======================
           FORM INPUTS - Apple HIG
           ====================== */

        .form-section {
          margin-bottom: 16px;
        }

        .form-row.two-col {
          flex-direction: column;
          gap: 16px;
        }

        .form-section label {
          font-size: 13px;
          font-weight: 500;
          margin-bottom: 6px;
          color: #6B7280;
          text-transform: uppercase;
          letter-spacing: 0.02em;
        }

        /* Input fields - 48px height for easy tapping */
        .form-section input,
        .form-section select,
        .form-section textarea {
          padding: 14px 16px;
          font-size: 17px;
          border-radius: 12px;
          min-height: 48px;
        }

        .form-section input:focus,
        .form-section select:focus,
        .form-section textarea:focus {
          outline: none;
          box-shadow: 0 0 0 3px rgba(var(--primary-rgb), 0.2);
        }

        /* Date picker - 44px touch targets */
        .date-picker-grid {
          gap: 8px;
        }

        .date-picker-grid select {
          padding: 14px 12px;
          font-size: 17px;
          min-height: 48px;
          border-radius: 12px;
        }

        /* ======================
           BUTTONS - 44pt MINIMUM
           Primary: 50px for bottom actions
           ====================== */

        .sdk-btn {
          padding: 14px 20px;
          font-size: 17px;
          border-radius: 12px;
          min-height: 44px;
          font-weight: 500;
        }

        .sdk-btn.primary {
          padding: 16px 24px;
          font-size: 17px;
          font-weight: 600;
          min-height: 50px;
          border-radius: 14px;
        }

        .sdk-btn.primary.full-width {
          margin-bottom: env(safe-area-inset-bottom);
        }

        /* Back button */
        .back-button {
          min-height: 44px;
          padding: 12px 16px;
          font-size: 17px;
        }

        /* ======================
           REVIEW STEP
           ====================== */

        .review-step .checklist-item {
          padding: 16px;
          margin-bottom: 10px;
          border-radius: 14px;
          min-height: 44px;
        }

        .review-step .checklist-item .checklist-icon {
          width: 32px;
          height: 32px;
        }

        .review-step .checklist-item span {
          font-size: 17px;
        }

        /* ======================
           COMPLETE STEP
           ====================== */

        .complete-step .step-icon-wrapper {
          width: 64px;
          height: 64px;
          margin-bottom: 16px;
        }

        .complete-step h2 {
          font-size: 22px;
        }

        .complete-step .step-subtitle {
          font-size: 17px;
        }

        /* ======================
           LIVENESS STEPS
           ====================== */

        .liveness-container {
          padding: 16px;
        }

        .liveness-instruction {
          font-size: 17px;
          margin-bottom: 12px;
        }

        .liveness-video-container {
          max-height: 45vh;
          max-height: 45dvh;
          border-radius: 16px;
        }

        /* ======================
           PROCESSING STEP
           ====================== */

        .processing-step {
          padding: 24px 16px;
        }

        .processing-step .spinner {
          width: 48px;
          height: 48px;
        }

        .processing-step p {
          font-size: 17px;
        }

        /* ======================
           QUESTIONNAIRE
           ====================== */

        .questionnaire-step .question-card {
          padding: 16px;
          margin-bottom: 12px;
          border-radius: 14px;
        }

        .questionnaire-step .question-label {
          font-size: 17px;
          font-weight: 500;
          margin-bottom: 12px;
        }

        .questionnaire-step .question-options {
          gap: 10px;
        }

        .questionnaire-step .option-btn {
          padding: 14px 16px;
          font-size: 17px;
          min-height: 48px;
          border-radius: 12px;
        }

        /* ======================
           POWERED BY BADGE
           ====================== */

        .powered-by-badge {
          padding: 12px 0;
          padding-bottom: calc(12px + env(safe-area-inset-bottom));
        }

        .powered-by-badge a {
          font-size: 13px;
        }

        /* ======================
           ERROR MESSAGES
           ====================== */

        .error-message {
          font-size: 15px;
          padding: 14px 16px;
          border-radius: 12px;
          min-height: 44px;
        }

        /* ======================
           CAMERA CONTROLS - 44px
           ====================== */

        .camera-controls button {
          min-width: 44px;
          min-height: 44px;
        }

        .camera-btn.capture {
          width: 64px;
          height: 64px;
        }

        .camera-btn.cancel {
          width: 44px;
          height: 44px;
        }

        /* ======================
           MODAL/OVERLAY BUTTONS
           ====================== */

        .preview-overlay button,
        .camera-guidance {
          min-height: 44px;
          font-size: 15px;
        }
      }

    `}</style>
  );
}

// Export components for custom implementations
export { ConsentStep, PersonalInfoStep, DocumentStep, SelfieStep, ReviewStep, CompleteStep, QuestionnaireStep, ProcessingStep };
